// src/components/settings/ImportWizardV2.js
//
// Data Import Wizard — V2.
//
// Phases (customer flow — Rounds 1+2):
//   1. upload      — drag/drop files, upload to Storage
//   2. detecting   — calls importDetectFiles
//   3. review      — show detected files; manual mapping for Tier-2 sources
//   4. planning    — calls importPlanCustomers (dry-run)
//   5. plan_review — show plan summary, resolve ambiguous matches
//   6. executing   — calls importExecuteCustomers in chunks; live progress
//   7. completed   — success state. If qb_sales files present, offers
//                    a "Continue to invoices" CTA into the invoice sub-flow.
//   8. failed      — error state
//   9. cancelled   — soft-cancel state (records up to that point are kept)
//
// Phases (invoice flow — Round 3b):
//  10. inv_planning      — calls importPlanInvoices
//  11. inv_plan_review   — show invoice plan summary card
//  12. inv_unresolved    — grouped-by-name UI for unmatched invoices
//                          (skipped if zero unresolved)
//  13. inv_executing     — calls importExecuteInvoices in chunks across two
//                          internal phases (A: invoices, B: importedPayments)
//  14. inv_completed     — success state for invoice flow
//  15. inv_cancelled     — soft-cancel during invoice execute
//  16. inv_failed        — invoice flow error state
//
// Architecture:
//   - Single component, all phases as conditional renders driven by `phase`
//   - Files upload directly client-side to Firebase Storage
//   - Single CF call for detect + plan + each execute chunk
//   - onSnapshot on the import doc drives live progress display in 'executing'
//     and 'inv_executing'
//   - Cancel is a direct Firestore client-side write to imports/{importId}.cancelRequested
//
// ROUND 2b VERIFIER: bd92f1c0-round2b-import-wizard-fix2-eslint
// ROUND 3b VERIFIER: 1761744000-round3b-invoice-wizard
//   (Marcus: grep both strings after saving to confirm you have the latest version)

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, updateDoc, collection, getDocs } from 'firebase/firestore';
import { db, functions } from '../../firebase/config';
import { getStorage } from 'firebase/storage';
import {
    UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, AlertOctagon,
    Info, ChevronDown, ChevronRight, X, ArrowRight, Loader2, Lock,
    Users, Receipt, ArrowLeftRight, Edit3, Plus,
    Lightbulb, FileText, RefreshCcw, SkipForward,
    // ROUND 4b VERIFIER: 1761900000-round4b-deals-wizard
    Calendar, Briefcase, Bell, Clock, History as HistoryIcon,
} from 'lucide-react';

// ----- Constants -----
const HIGH_LEVEL_ROLES = ['Admin', 'Manager', 'Owner/System Admin', 'Owner / System Admin', 'Owner', 'System Admin'];
const ALLOWED_EXTENSIONS = ['.xls', '.xlsx', '.csv'];
const MAX_FILES = 10;
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

const MAPPABLE_FIELDS = [
    { fieldId: 'firstName',  label: 'First Name' },
    { fieldId: 'lastName',   label: 'Last Name' },
    { fieldId: 'company',    label: 'Company' },
    { fieldId: 'email',      label: 'Email' },
    { fieldId: 'phone',      label: 'Phone' },
    { fieldId: 'cell_phone', label: 'Cell Phone' },
    { fieldId: 'address1',   label: 'Address Line 1' },
    { fieldId: 'address2',   label: 'Address Line 2' },
    { fieldId: 'city',       label: 'City' },
    { fieldId: 'state',      label: 'State / Province' },
    { fieldId: 'zip',        label: 'Zip / Postal Code' },
    { fieldId: 'country',    label: 'Country' },
    { fieldId: 'jobNote',    label: 'Notes' },
    { fieldId: 'tags',       label: 'Tags' },
];

const IDENTIFYING_FIELDS = ['firstName', 'lastName', 'company', 'email', 'phone'];

// ----- Helpers -----
const fileExtension = (name) => {
    const m = name.toLowerCase().match(/\.[a-z0-9]+$/);
    return m ? m[0] : '';
};

const isAllowedFile = (file) =>
    ALLOWED_EXTENSIONS.includes(fileExtension(file.name)) && file.size <= MAX_FILE_SIZE_BYTES;

const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (val) => {
    if (!val) return '';
    const d = val.toDate ? val.toDate() : new Date(val);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatCurrency = (n) => {
    const v = typeof n === 'number' ? n : parseFloat(n);
    if (isNaN(v)) return '$0.00';
    return v.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const generateImportId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return `imp_${crypto.randomUUID().replace(/-/g, '').slice(0, 24)}`;
    }
    return `imp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

// ----- Component -----
const ImportWizardV2 = ({ currentUserProfile }) => {
    const userRole = currentUserProfile?.role || '';
    const orgId = currentUserProfile?.organizationId;
    const isAuthorized = HIGH_LEVEL_ROLES.includes(userRole);
    const navigate = useNavigate();

    // --- State ---
    const [phase, setPhase] = useState('upload');
    const [uploads, setUploads] = useState([]);
    const [importId, setImportId] = useState(() => generateImportId());
    const [isDragActive, setIsDragActive] = useState(false);
    const [detectError, setDetectError] = useState(null);
    const [detectionFiles, setDetectionFiles] = useState([]);
    const [expandedSamples, setExpandedSamples] = useState({});
    const [userMappings, setUserMappings] = useState({});

    // Plan + execute state
    const [planError, setPlanError] = useState(null);
    const [plan, setPlan] = useState(null);
    // ambiguousDecisions: { [reviewIndex]: 'merge' | 'separate' | undefined }
    // undefined means "not yet decided" — counts as "skipped" if user proceeds.
    const [ambiguousDecisions, setAmbiguousDecisions] = useState({});
    const [executeError, setExecuteError] = useState(null);
    const [executeProgress, setExecuteProgress] = useState({ chunksCompleted: 0, totalChunks: 0, stats: null });
    const [executeStartTime, setExecuteStartTime] = useState(null);
    const [cancelInFlight, setCancelInFlight] = useState(false);

    // ROUND 3b VERIFIER: 1761744000-round3b-state
    // Round 3b: Invoice flow state. Coexists with customer flow state — the
    // wizard re-uses the same import doc but transitions through a parallel
    // set of phases after the customer flow completes.
    const [invPlanError, setInvPlanError] = useState(null);
    const [invPlan, setInvPlan] = useState(null);
    // groupDecisions: { [qbCustomerName]: { action: 'assign'|'skip', customerId?: string } }
    // Required for every group before Continue. No default ('skip' or 'assign'
    // must be explicit per the design walkthrough — no silent skipping).
    const [groupDecisions, setGroupDecisions] = useState({});
    // Per-invoice overrides within an expanded group:
    // { [qbCustomerName]: { [rowIndex]: 'skip' | { customerId: string } } }
    const [invoiceOverrides, setInvoiceOverrides] = useState({});
    // expandedGroups: which group cards have "Show all N invoices" toggled open
    const [expandedGroups, setExpandedGroups] = useState({});
    const [invExecuteError, setInvExecuteError] = useState(null);
    const [invExecuteProgress, setInvExecuteProgress] = useState({
        chunksCompleted: 0, totalChunks: 0, invoiceChunks: 0, paymentChunks: 0,
        phase: 'A', stats: null,
    });
    const [invExecuteStartTime, setInvExecuteStartTime] = useState(null);
    const [invCancelInFlight, setInvCancelInFlight] = useState(false);
    // Existing customers list for the typeahead picker. Loaded once when
    // entering inv_unresolved phase.
    const [existingCustomers, setExistingCustomers] = useState([]);
    const [existingCustomersLoading, setExistingCustomersLoading] = useState(false);
    const invCancelRequestedRef = useRef(false);

    // ROUND 4b VERIFIER: 1761900000-round4b-deals-wizard
    // Round 4b: Jobs & follow-ups flow state. Coexists with customer + invoice
    // flow state. The wizard re-uses the same import doc but transitions
    // through a parallel set of phases after either customer flow OR invoice
    // flow completes (the "Continue to jobs & follow-ups" CTA appears in both
    // CompletedPhase and InvCompletedPhase when pipedrive_deals files are
    // present in the import session).
    //
    // Internal naming: phase strings, state names, and backend CFs all use
    // "deal" terminology because that matches the source-data language
    // (Pipedrive Deals export). User-facing UI copy avoids "deal" entirely
    // and uses Spatchy-native terms: "Scheduled jobs", "Pending jobs",
    // "Follow-up appointments", and "Imported History records".
    const [dealPlanError, setDealPlanError] = useState(null);
    const [dealPlan, setDealPlan] = useState(null);
    // dealGroupDecisions: { [orgName]: { action: 'assign'|'skip', customerId?: string } }
    const [dealGroupDecisions, setDealGroupDecisions] = useState({});
    const [dealExpandedGroups, setDealExpandedGroups] = useState({});
    const [dealExecuteError, setDealExecuteError] = useState(null);
    const [dealExecuteProgress, setDealExecuteProgress] = useState({
        chunksCompleted: 0, totalChunks: 0,
        parentChunks: 0, followUpChunks: 0, auditChunks: 0,
        phase: 'A', stats: null,
    });
    const [dealExecuteStartTime, setDealExecuteStartTime] = useState(null);
    const [dealCancelInFlight, setDealCancelInFlight] = useState(false);
    // skipAnomalies controls whether the 4 Closed-Lost-with-future-close rows
    // become parent jobs. Default true (recommended) — user can opt-in to
    // import them via the toggle on the plan-review screen. Either way they
    // get audit doc rows for full traceability.
    const [skipDealAnomalies, setSkipDealAnomalies] = useState(true);
    const dealCancelRequestedRef = useRef(false);

    // --- Line Items (Products & Services) sub-flow state --- [seam a9d3f7e2]
    const [liPlan, setLiPlan] = useState(null);
    const [liImport, setLiImport] = useState({}); // reviewItem key -> true (import) / false (skip)
    const [liPlanError, setLiPlanError] = useState(null);
    const [liExecuteError, setLiExecuteError] = useState(null);
    const [liExecuteProgress, setLiExecuteProgress] = useState({ chunksCompleted: 0, totalChunks: 0, stats: null });
    const [liExecuteStartTime, setLiExecuteStartTime] = useState(null);
    const [liCancelInFlight, setLiCancelInFlight] = useState(false);
    const liCancelRequestedRef = useRef(false);

    const fileInputRef = useRef(null);
    const cancelRequestedRef = useRef(false);

    // --- Derived ---
    const allUploadsDone = uploads.length > 0 && uploads.every(u => u.status === 'done' || u.status === 'failed');
    const successfulUploads = uploads.filter(u => u.status === 'done');
    const canDetect = phase === 'upload' && allUploadsDone && successfulUploads.length > 0;

    const isFileReady = useCallback((fileResult) => {
        if (fileResult.mappingMode === 'auto') return true;
        if (fileResult.mappingMode === 'failed') return false;
        if (fileResult.mappingMode === 'manual_required') {
            const m = userMappings[fileResult.filename] || {};
            const mappedFields = Object.values(m).filter(Boolean);
            return mappedFields.some(f => IDENTIFYING_FIELDS.includes(f));
        }
        return false;
    }, [userMappings]);

    const allFilesReady = useMemo(() => {
        if (detectionFiles.length === 0) return false;
        return detectionFiles.every(isFileReady);
    }, [detectionFiles, isFileReady]);

    // For Phase 4: count of decided ambiguous candidates (Round 6.5 — per-candidate)
    // ROUND 6.5 VERIFIER: 1762110000-round6_5-per-candidate-review
    // ROUND 6.5-fix2 VERIFIER: 1762120000-round6_5-implicit-decisions
    // A candidate is "decided" if either (a) it has its own explicit decision,
    // OR (b) it's referenced by another candidate's decision (merge_with_suggestions
    // or match_to_candidate). The latter is "implicitly decided" — we don't need
    // to ask the user about a candidate that's already part of a merge group
    // they expressed elsewhere.
    const ambiguousCandidatesCount = plan?.ambiguousCandidates?.length || 0;
    const decidedCount = useMemo(() => {
        if (ambiguousCandidatesCount === 0) return 0;
        const candidates = plan?.ambiguousCandidates || [];
        // Build the implicit-decided set from all explicit decisions
        const implicitSet = new Set();
        for (const v of Object.values(ambiguousDecisions)) {
            if (!v || typeof v !== 'object') continue;
            if (v.type === 'merge_with_suggestions' && Array.isArray(v.candidateSuggestionIndices)) {
                for (const idx of v.candidateSuggestionIndices) implicitSet.add(idx);
            } else if (v.type === 'match_to_candidate' && typeof v.targetCandidateIndex === 'number') {
                implicitSet.add(v.targetCandidateIndex);
            }
        }
        let n = 0;
        for (const c of candidates) {
            const v = ambiguousDecisions[c.candidateIndex];
            const hasExplicit = v && typeof v === 'object' &&
                ['create_new', 'merge_with_suggestions', 'match_to_existing', 'match_to_candidate'].includes(v.type);
            const isImplicit = implicitSet.has(c.candidateIndex);
            if (hasExplicit || isImplicit) n++;
        }
        return n;
    }, [ambiguousCandidatesCount, ambiguousDecisions, plan]);
    const skippedCount = ambiguousCandidatesCount - decidedCount;

    // --- Effects ---

    // Subscribe to import doc once detection starts (drives live status display)
    useEffect(() => {
        if (phase !== 'detecting' || !orgId) return undefined;

        const unsub = onSnapshot(
            doc(db, `organizations/${orgId}/imports/${importId}`),
            (snap) => {
                if (!snap.exists()) return;
                const data = snap.data();
                if (data.status === 'detected' && Array.isArray(data.files)) {
                    const initialMappings = {};
                    data.files.forEach(f => {
                        if (f.mappingMode === 'manual_required' && f.smartPrefillMapping) {
                            initialMappings[f.filename] = { ...f.smartPrefillMapping };
                        }
                    });
                    setUserMappings(prev => ({ ...initialMappings, ...prev }));
                    setDetectionFiles(data.files);
                    setPhase('review');
                } else if (data.status === 'failed') {
                    setDetectError('Detection failed. Please try again.');
                    setPhase('upload');
                }
            },
            (err) => {
                console.error('ImportWizardV2: import doc subscription error', err);
                setDetectError('Could not load detection status. Please try again.');
                setPhase('upload');
            }
        );
        return () => unsub();
    }, [phase, orgId, importId]);

    // Subscribe during execute — live progress
    useEffect(() => {
        if (phase !== 'executing' || !orgId) return undefined;
        const unsub = onSnapshot(
            doc(db, `organizations/${orgId}/imports/${importId}`),
            (snap) => {
                if (!snap.exists()) return;
                const data = snap.data();
                setExecuteProgress({
                    chunksCompleted: data.chunksCompleted || 0,
                    totalChunks: data.totalChunks || 0,
                    stats: data.executionStats || null,
                });
                if (data.status === 'completed') {
                    setPhase('completed');
                } else if (data.status === 'cancelled') {
                    setPhase('cancelled');
                } else if (data.status === 'failed') {
                    setExecuteError('Import failed. See details below.');
                    setPhase('failed');
                }
            },
            (err) => {
                console.error('ImportWizardV2: execute subscription error', err);
            }
        );
        return () => unsub();
    }, [phase, orgId, importId]);

    // Subscribe during line-items execute — live progress [seam a9d3f7e2]
    useEffect(() => {
        if (phase !== 'li_executing' || !orgId) return undefined;
        const unsub = onSnapshot(
            doc(db, `organizations/${orgId}/imports/${importId}`),
            (snap) => {
                if (!snap.exists()) return;
                const data = snap.data();
                setLiExecuteProgress({
                    chunksCompleted: data.lineItemsChunksCompleted || 0,
                    totalChunks: data.lineItemsTotalChunks || 0,
                    stats: data.lineItemsExecutionStats || null,
                });
                if (data.status === 'lineitems_completed') {
                    setPhase('li_completed');
                } else if (data.status === 'lineitems_cancelled') {
                    setPhase('li_cancelled');
                } else if (data.status === 'lineitems_failed') {
                    setLiExecuteError('Import failed. See details below.');
                    setPhase('li_failed');
                }
            },
            (err) => {
                console.error('ImportWizardV2: line-items subscription error', err);
            }
        );
        return () => unsub();
    }, [phase, orgId, importId]);

    // ROUND 3b VERIFIER: 1761744000-round3b-onsnapshot
    // Subscribe during invoice execute — live progress for Phase A + B
    useEffect(() => {
        if (phase !== 'inv_executing' || !orgId) return undefined;
        const unsub = onSnapshot(
            doc(db, `organizations/${orgId}/imports/${importId}`),
            (snap) => {
                if (!snap.exists()) return;
                const data = snap.data();
                const chunksCompleted = data.invoicesChunksCompleted || 0;
                const totalChunks = data.invoicesTotalChunks || 0;
                const invoiceChunks = data.invoiceChunkBoundary || 0;
                const paymentChunks = totalChunks - invoiceChunks;
                const currentPhase = chunksCompleted < invoiceChunks ? 'A' : 'B';
                setInvExecuteProgress({
                    chunksCompleted,
                    totalChunks,
                    invoiceChunks,
                    paymentChunks,
                    phase: currentPhase,
                    stats: data.invoicesExecutionStats || null,
                });
                if (data.status === 'invoices_completed') {
                    setPhase('inv_completed');
                } else if (data.status === 'invoices_cancelled') {
                    setPhase('inv_cancelled');
                } else if (data.status === 'invoices_failed') {
                    setInvExecuteError('Invoice import failed. See details below.');
                    setPhase('inv_failed');
                }
            },
            (err) => {
                console.error('ImportWizardV2: inv_executing subscription error', err);
            }
        );
        return () => unsub();
    }, [phase, orgId, importId]);

    // ROUND 4b VERIFIER: 1761900000-round4b-deals-wizard
    // Subscribe during deal_executing — live progress for Phase A (parent
    // jobs), Phase B (follow-up appointments), Phase C (imported history +
    // status promotion). Mirrors inv_executing pattern.
    useEffect(() => {
        if (phase !== 'deal_executing' || !orgId) return undefined;
        const unsub = onSnapshot(
            doc(db, `organizations/${orgId}/imports/${importId}`),
            (snap) => {
                if (!snap.exists()) return;
                const data = snap.data();
                const chunksCompleted = data.dealsChunksCompleted || 0;
                const totalChunks = data.dealsTotalChunks || 0;
                const parentChunks = data.dealsParentChunks || 0;
                const followUpChunks = data.dealsFollowUpChunks || 0;
                const auditChunks = data.dealsAuditChunks || 0;
                let currentPhase = 'A';
                if (chunksCompleted >= parentChunks + followUpChunks) currentPhase = 'C';
                else if (chunksCompleted >= parentChunks) currentPhase = 'B';
                setDealExecuteProgress({
                    chunksCompleted,
                    totalChunks,
                    parentChunks,
                    followUpChunks,
                    auditChunks,
                    phase: currentPhase,
                    stats: data.dealsExecutionStats || null,
                });
                if (data.status === 'deals_completed') {
                    setPhase('deal_completed');
                } else if (data.status === 'deals_cancelled') {
                    setPhase('deal_cancelled');
                } else if (data.status === 'deals_failed') {
                    setDealExecuteError('Jobs & follow-ups import failed. See details below.');
                    setPhase('deal_failed');
                }
            },
            (err) => {
                console.error('ImportWizardV2: deal_executing subscription error', err);
            }
        );
        return () => unsub();
    }, [phase, orgId, importId]);

    // ROUND 3b VERIFIER: 1761744000-round3b-customer-load-fix1
    // Load existing customers for the unresolved-groups typeahead picker.
    //
    // FIX: previously triggered by `phase === 'inv_plan_review'`, but this
    // created a race condition — if the user advanced to `inv_unresolved`
    // before load completed, the cleanup function cancelled the request and
    // the load never completed (effect re-fire was blocked by the phase
    // guard on the new phase).
    //
    // New trigger: load whenever an invPlan exists with unresolved groups
    // and we don't have customers yet. Phase-independent.
    //
    // Also switched from dynamic `await import('firebase/firestore')` to
    // static imports at the top — dynamic import was an unnecessary risk
    // for a method we use elsewhere via static import already.
    useEffect(() => {
        if (!orgId) return;
        // ROUND 4b VERIFIER: 1761900000-round4b-deals-wizard
        // Trigger condition extended to include dealPlan unresolved groups
        // so the same customer picker is loaded for both invoice and deal
        // unresolved-resolution flows.
        const needsForInv = !!invPlan?.unresolvedGroups?.length;
        const needsForDeal = !!dealPlan?.unresolvedGroups?.length;
        if (!needsForInv && !needsForDeal) return;
        if (existingCustomers.length > 0 || existingCustomersLoading) return;
        let cancelled = false;
        (async () => {
            setExistingCustomersLoading(true);
            try {
                const snap = await getDocs(collection(db, `organizations/${orgId}/customers`));
                if (cancelled) return;
                const list = snap.docs.map(d => {
                    const data = d.data();
                    const display = data.company
                        || `${data.firstName || ''} ${data.lastName || ''}`.trim()
                        || data.name
                        || '(unnamed)';
                    return { id: d.id, name: display, raw: data };
                });
                list.sort((a, b) => a.name.localeCompare(b.name));
                // Diagnostic: helps confirm load worked + see what name shape we got.
                // Safe to leave in production — fires once per import session.
                console.log(`[ImportWizardV2] loaded ${list.length} customers for picker`,
                    list.length > 0 ? `(first: "${list[0].name}")` : '');
                setExistingCustomers(list);
            } catch (err) {
                console.error('[ImportWizardV2] customer load failed', err);
            } finally {
                if (!cancelled) setExistingCustomersLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [orgId, invPlan, dealPlan, existingCustomers.length, existingCustomersLoading]);

    // --- Handlers: Upload phase ---
    const handleFiles = useCallback((fileList) => {
        if (!fileList || fileList.length === 0) return;
        setDetectError(null);

        const remainingSlots = MAX_FILES - uploads.length;
        const newFiles = Array.from(fileList).slice(0, remainingSlots);

        const newUploads = newFiles.map(file => {
            const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            if (!isAllowedFile(file)) {
                const ext = fileExtension(file.name) || '(none)';
                let error;
                if (file.size > MAX_FILE_SIZE_BYTES) {
                    error = `File exceeds 50MB limit (${formatBytes(file.size)}).`;
                } else {
                    error = `Unsupported file type ${ext}. Use Excel or CSV.`;
                }
                return { id, file, status: 'failed', progress: 0, error, storagePath: null };
            }
            return { id, file, status: 'pending', progress: 0, error: null, storagePath: null };
        });

        setUploads(prev => [...prev, ...newUploads]);

        const storage = getStorage();
        newUploads.forEach(upload => {
            if (upload.status !== 'pending') return;
            const storagePath = `organizations/${orgId}/imports/${importId}/${upload.file.name}`;
            const storageRef = ref(storage, storagePath);
            const task = uploadBytesResumable(storageRef, upload.file);

            setUploads(prev => prev.map(u =>
                u.id === upload.id ? { ...u, status: 'uploading', storagePath } : u
            ));

            task.on(
                'state_changed',
                (snap) => {
                    const progress = (snap.bytesTransferred / snap.totalBytes) * 100;
                    setUploads(prev => prev.map(u =>
                        u.id === upload.id ? { ...u, progress } : u
                    ));
                },
                (err) => {
                    console.error('ImportWizardV2: upload error', err);
                    setUploads(prev => prev.map(u =>
                        u.id === upload.id ? { ...u, status: 'failed', error: 'Upload failed.' } : u
                    ));
                },
                () => {
                    setUploads(prev => prev.map(u =>
                        u.id === upload.id ? { ...u, status: 'done', progress: 100 } : u
                    ));
                }
            );
        });
    }, [uploads.length, importId, orgId]);

    const handleDrop = useCallback((e) => {
        e.preventDefault(); e.stopPropagation();
        setIsDragActive(false);
        if (e.dataTransfer?.files) handleFiles(e.dataTransfer.files);
    }, [handleFiles]);

    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragActive(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragActive(false); };
    const handleBrowseClick = () => fileInputRef.current?.click();
    const handleInputChange = (e) => {
        if (e.target.files) handleFiles(e.target.files);
        e.target.value = '';
    };
    const removeUpload = (id) => setUploads(prev => prev.filter(u => u.id !== id));

    // --- Handlers: Detect ---
    const handleDetect = async () => {
        if (!canDetect) return;
        setDetectError(null);
        setPhase('detecting');
        try {
            const callable = httpsCallable(functions, 'importDetectFiles');
            const result = await callable({
                importId,
                storagePaths: successfulUploads.map(u => u.storagePath),
            });
            if (result?.data?.files && phase === 'detecting') {
                const initialMappings = {};
                result.data.files.forEach(f => {
                    if (f.mappingMode === 'manual_required' && f.smartPrefillMapping) {
                        initialMappings[f.filename] = { ...f.smartPrefillMapping };
                    }
                });
                setUserMappings(prev => ({ ...initialMappings, ...prev }));
                setDetectionFiles(result.data.files);
                setPhase('review');
            }
        } catch (err) {
            console.error('ImportWizardV2: detect call failed', err);
            const message = err?.code === 'permission-denied'
                ? 'Only Owners, Admins, and Managers can import data.'
                : 'Detection failed. Please try again.';
            setDetectError(message);
            setPhase('upload');
        }
    };

    // --- Handlers: Plan ---
    const handleContinueToPlan = async () => {
        setPlanError(null);
        setPhase('planning');
        try {
            const callable = httpsCallable(functions, 'importPlanCustomers');
            const r = await callable({ importId, userMappings });
            const planData = r.data?.plan || null;
            if (!planData) {
                setPlanError('Plan response was empty. Please try again.');
                setPhase('review');
                return;
            }
            setPlan(planData);
            setAmbiguousDecisions({}); // reset on new plan
            // ROUND 6 VERIFIER: 1762100000-round6-ambiguous-search-bar
            // Seed picker customer list from plan response. Mirror of
            // handleStartInvoiceImport / handleStartDealImport. Used by
            // the search override on each CandidateReviewCard.
            const customersFromPlan = Array.isArray(planData.existingCustomers)
                ? planData.existingCustomers
                : null;
            if (customersFromPlan) {
                console.log(`[ImportWizardV2] customer-plan picker seeded with ${customersFromPlan.length} customers`,
                    customersFromPlan.length > 0 ? `(first: "${customersFromPlan[0].name}")` : '');
                setExistingCustomers(customersFromPlan);
            } else {
                console.log('[ImportWizardV2] customer plan response did not include customers — picker will fall back to client-side load. Did you redeploy importPlanCustomers?');
            }
            setPhase('plan_review');
        } catch (err) {
            console.error('ImportWizardV2: plan call failed', err);
            const message = err?.code === 'permission-denied'
                ? 'Only Owners, Admins, and Managers can import data.'
                : (err?.message || 'Plan failed. Please try again.');
            setPlanError(message);
            setPhase('review');
        }
    };

    // --- Handlers: Execute ---
    const handleStartImport = async () => {
        setExecuteError(null);
        setPhase('executing');
        setExecuteStartTime(Date.now());
        cancelRequestedRef.current = false;

        // ROUND 6.5 VERIFIER: 1762110000-round6_5-per-candidate-review
        // Per-candidate decision serialization. Iterate candidates (not pairs)
        // and emit decisions in the new shape:
        //   { candidateIndex, decision: 'create_new' }
        //   { candidateIndex, decision: 'merge_with_candidates', targetCandidateIndices: [...] }
        //   { candidateIndex, decision: 'match_to_existing', existingCustomerId }
        //   { candidateIndex, decision: 'match_to_candidate', targetCandidateIndex }
        // Undecided / unknown shapes are no-ops (default to create-as-new on backend).
        const decisions = [];
        const ambiguousCandidates = plan?.ambiguousCandidates || [];
        for (const c of ambiguousCandidates) {
            const choice = ambiguousDecisions[c.candidateIndex];
            if (!choice || typeof choice !== 'object') continue;
            if (choice.type === 'create_new') {
                decisions.push({ candidateIndex: c.candidateIndex, decision: 'create_new' });
            } else if (choice.type === 'merge_with_suggestions') {
                // ROUND 7 VERIFIER: 1762130000-round7-fuzzy-vs-existing-db
                // The merge can include candidate-side targets, an existing-DB-
                // customer target, or both. Send all three fields; backend
                // applies them additively.
                const decisionPayload = {
                    candidateIndex: c.candidateIndex,
                    decision: 'merge_with_candidates',
                };
                if (Array.isArray(choice.candidateSuggestionIndices) && choice.candidateSuggestionIndices.length > 0) {
                    decisionPayload.targetCandidateIndices = [...choice.candidateSuggestionIndices];
                }
                if (choice.existingCustomerId) {
                    decisionPayload.existingCustomerId = choice.existingCustomerId;
                }
                // Skip empty payloads (no candidates AND no DB customer) — defensive
                if (decisionPayload.targetCandidateIndices || decisionPayload.existingCustomerId) {
                    decisions.push(decisionPayload);
                }
            } else if (choice.type === 'match_to_existing' && choice.existingCustomerId) {
                decisions.push({
                    candidateIndex: c.candidateIndex,
                    decision: 'match_to_existing',
                    existingCustomerId: choice.existingCustomerId,
                });
            } else if (choice.type === 'match_to_candidate' && typeof choice.targetCandidateIndex === 'number') {
                decisions.push({
                    candidateIndex: c.candidateIndex,
                    decision: 'match_to_candidate',
                    targetCandidateIndex: choice.targetCandidateIndex,
                });
            }
        }

        try {
            const callable = httpsCallable(functions, 'importExecuteCustomers');
            let chunkIndex = 0;
            let moreChunks = true;
            // Hard cap: 50 chunks * 200/chunk = 10,000 customers. Plenty of safety margin.
            while (moreChunks && chunkIndex < 50 && !cancelRequestedRef.current) {
                const payload = { importId, chunkIndex };
                if (chunkIndex === 0) payload.decisions = decisions;
                // eslint-disable-next-line no-await-in-loop
                const r = await callable(payload);
                const d = r.data || {};
                moreChunks = !!d.moreChunks;
                chunkIndex = d.chunksCompleted || (chunkIndex + 1);
                if (d.status === 'cancelled') {
                    setPhase('cancelled');
                    return;
                }
                if (d.status === 'failed') {
                    setExecuteError('Import failed during execution.');
                    setPhase('failed');
                    return;
                }
            }
            // The onSnapshot listener will flip phase to 'completed' when status updates.
            // If we exit the loop without phase change, force it now.
            //
            // Three exit conditions:
            //   1. moreChunks === false → import is done → phase: 'completed'
            //   2. cancelRequestedRef.current === true → user cancelled → phase: 'cancelled'
            //      (handleCancelExecute already wrote status:'cancelled' to Firestore;
            //       the onSnapshot listener will flip the phase, but we also transition
            //       defensively here in case the listener races.)
            //   3. chunkIndex >= 50 → safety guard hit → unusual; treat as done
            if (cancelRequestedRef.current) {
                setTimeout(() => {
                    setPhase(p => (p === 'executing' ? 'cancelled' : p));
                }, 500);
            } else if (!moreChunks) {
                setTimeout(() => {
                    setPhase(p => (p === 'executing' ? 'completed' : p));
                }, 500);
            }
        } catch (err) {
            console.error('ImportWizardV2: execute call failed', err);
            const message = err?.message || 'Import failed. Please try again.';
            setExecuteError(message);
            setPhase('failed');
        }
    };

    // --- Handlers: Cancel during execute ---
    const handleCancelExecute = async () => {
        if (cancelInFlight) return;
        if (!window.confirm('Cancel the import? Any customers already imported will remain. Imports already in progress will finish their current batch before stopping.')) return;
        setCancelInFlight(true);
        cancelRequestedRef.current = true;
        try {
            // Write BOTH the cancel flag AND status:'cancelled' directly. The status
            // write is what triggers the UI to transition (via onSnapshot) — without
            // it, the UI hangs on 'executing' if no further chunks run after the
            // user clicks Cancel.
            //
            // The cancelRequested flag is still set as a defensive belt-and-suspenders
            // — if any in-flight chunk's CF read happens AFTER our write but BEFORE
            // our status update, the CF will see the flag and stop.
            await updateDoc(
                doc(db, `organizations/${orgId}/imports/${importId}`),
                { cancelRequested: true, status: 'cancelled' }
            );
            // The onSnapshot effect will pick up status:'cancelled' and flip the phase.
            // As a final safety, if the listener somehow misses, force the transition
            // after a short delay.
            setTimeout(() => {
                setPhase(p => (p === 'executing' ? 'cancelled' : p));
            }, 1500);
        } catch (err) {
            console.error('ImportWizardV2: cancel write failed', err);
            // Even if the write failed, we set the local ref so the loop bails.
            // Force the phase transition locally so the UI doesn't hang.
            setExecuteError('Could not signal cancel to the server. Local cancel applied.');
            setPhase('cancelled');
        } finally {
            setCancelInFlight(false);
        }
    };

    // --- Handlers: Line Items (Products & Services) --- [seam a9d3f7e2]
    const handleLiToggle = (key) => {
        setLiImport(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handlePlanLineItems = async () => {
        setLiPlanError(null);
        setPhase('li_planning');
        try {
            const callable = httpsCallable(functions, 'importPlanLineItems');
            const r = await callable({ importId });
            const planData = r.data?.plan || null;
            if (!planData) {
                setLiPlanError('Plan response was empty. Please try again.');
                setPhase('review');
                return;
            }
            setLiPlan(planData);
            // Default: import everything flagged EXCEPT suggested-skips and
            // no-name rows (which can't be imported at all).
            const initial = {};
            for (const it of (planData.reviewItems || [])) {
                initial[it.key] = it.reason === 'no_name' ? false : !it.suggestedSkip;
            }
            setLiImport(initial);
            setPhase('li_review');
        } catch (err) {
            console.error('ImportWizardV2: line-items plan call failed', err);
            const message = err?.code === 'permission-denied'
                ? 'Only Owners, Admins, and Managers can import data.'
                : (err?.message || 'Plan failed. Please try again.');
            setLiPlanError(message);
            setPhase('review');
        }
    };

    const handleStartLineItemImport = async () => {
        setLiExecuteError(null);
        // Keys of zero-price rows the user UNCHECKED — these get skipped.
        const skipKeys = (liPlan?.reviewItems || [])
            .filter(it => it.reason === 'no_price' && liImport[it.key] === false)
            .map(it => it.key);
        try {
            await updateDoc(
                doc(db, `organizations/${orgId}/imports/${importId}`),
                { lineItemsSkipKeys: skipKeys, lineItemsCancelRequested: false }
            );
        } catch (err) {
            console.error('ImportWizardV2: could not write line-items skip list', err);
            setLiExecuteError('Could not save your selections. Please try again.');
            return;
        }
        setPhase('li_executing');
        setLiExecuteStartTime(Date.now());
        liCancelRequestedRef.current = false;
        try {
            const callable = httpsCallable(functions, 'importExecuteLineItems');
            let chunkIndex = 0;
            let moreChunks = true;
            // Hard cap: 50 chunks * 200/chunk = 10,000 items.
            while (moreChunks && chunkIndex < 50 && !liCancelRequestedRef.current) {
                // eslint-disable-next-line no-await-in-loop
                const r = await callable({ importId, chunkIndex });
                const d = r.data || {};
                moreChunks = !!d.moreChunks;
                chunkIndex = d.chunksCompleted || (chunkIndex + 1);
                if (d.status === 'lineitems_cancelled') {
                    setPhase('li_cancelled');
                    return;
                }
                if (d.status === 'lineitems_failed') {
                    setLiExecuteError('Import failed during execution.');
                    setPhase('li_failed');
                    return;
                }
            }
            // onSnapshot flips phase on terminal status; transition defensively
            // here in case it races.
            if (liCancelRequestedRef.current) {
                setTimeout(() => {
                    setPhase(p => (p === 'li_executing' ? 'li_cancelled' : p));
                }, 500);
            } else if (!moreChunks) {
                setTimeout(() => {
                    setPhase(p => (p === 'li_executing' ? 'li_completed' : p));
                }, 500);
            }
        } catch (err) {
            console.error('ImportWizardV2: line-items execute call failed', err);
            setLiExecuteError(err?.message || 'Import failed. Please try again.');
            setPhase('li_failed');
        }
    };

    const handleCancelLineItemImport = async () => {
        if (liCancelInFlight) return;
        if (!window.confirm('Cancel the import? Any items already imported will remain. An in-progress batch will finish before stopping.')) return;
        setLiCancelInFlight(true);
        liCancelRequestedRef.current = true;
        try {
            await updateDoc(
                doc(db, `organizations/${orgId}/imports/${importId}`),
                { lineItemsCancelRequested: true, status: 'lineitems_cancelled' }
            );
            setTimeout(() => {
                setPhase(p => (p === 'li_executing' ? 'li_cancelled' : p));
            }, 1500);
        } catch (err) {
            console.error('ImportWizardV2: line-items cancel write failed', err);
            setLiExecuteError('Could not signal cancel to the server. Local cancel applied.');
            setPhase('li_cancelled');
        } finally {
            setLiCancelInFlight(false);
        }
    };

    // --- Handlers: Start over ---
    const handleStartOver = () => {
        setImportId(generateImportId());
        setPhase('upload');
        setUploads([]);
        setDetectionFiles([]);
        setExpandedSamples({});
        setUserMappings({});
        setDetectError(null);
        setPlan(null);
        setAmbiguousDecisions({});
        setPlanError(null);
        setExecuteError(null);
        setExecuteProgress({ chunksCompleted: 0, totalChunks: 0, stats: null });
        setExecuteStartTime(null);
        cancelRequestedRef.current = false;
        // line-items resets [seam a9d3f7e2]
        setLiPlan(null);
        setLiImport({});
        setLiPlanError(null);
        setLiExecuteError(null);
        setLiExecuteProgress({ chunksCompleted: 0, totalChunks: 0, stats: null });
        setLiExecuteStartTime(null);
        liCancelRequestedRef.current = false;
        // ROUND 3b VERIFIER: 1761744000-round3b-startover
        setInvPlan(null);
        setInvPlanError(null);
        setGroupDecisions({});
        setInvoiceOverrides({});
        setExpandedGroups({});
        setInvExecuteError(null);
        setInvExecuteProgress({
            chunksCompleted: 0, totalChunks: 0, invoiceChunks: 0, paymentChunks: 0,
            phase: 'A', stats: null,
        });
        setInvExecuteStartTime(null);
        setExistingCustomers([]);
        invCancelRequestedRef.current = false;
    };

    // --- Handlers: Mapping (Phase 3) + ambiguous decisions (Phase 4) ---
    const handleMappingChange = (filename, sourceColumn, targetField) => {
        setUserMappings(prev => ({
            ...prev,
            [filename]: { ...(prev[filename] || {}), [sourceColumn]: targetField || null },
        }));
    };
    const toggleSampleExpanded = (filename) => {
        setExpandedSamples(prev => ({ ...prev, [filename]: !prev[filename] }));
    };
    const handleAmbiguousDecision = (reviewIndex, decision) => {
        setAmbiguousDecisions(prev => ({ ...prev, [reviewIndex]: decision }));
    };
    const handleAmbiguousReset = (reviewIndex) => {
        setAmbiguousDecisions(prev => {
            const next = { ...prev };
            delete next[reviewIndex];
            return next;
        });
    };

    // ROUND 3b VERIFIER: 1761744000-round3b-handlers
    // ----- Invoice flow handlers -----

    // Called from CompletedPhase "Continue to invoices" CTA. Initiates
    // importPlanInvoices and transitions through inv_planning -> inv_plan_review.
    const handleStartInvoiceImport = async () => {
        setInvPlanError(null);
        setPhase('inv_planning');
        try {
            const callable = httpsCallable(functions, 'importPlanInvoices');
            const r = await callable({ importId });
            const planData = r.data?.plan || null;
            if (!planData) {
                setInvPlanError('Plan response was empty. Please try again.');
                setPhase('completed');
                return;
            }
            // ROUND 3b VERIFIER: 1761744000-round3b-customers-from-plan-fix2
            // Seed picker customer list from plan response (backend includes it
            // in plan.existingCustomers as of the customers-in-plan-fix2 backend
            // change). This eliminates the client-side load entirely. The
            // separate load useEffect remains as a fallback for cases where
            // the backend response is missing this field (e.g., if functions
            // weren't redeployed yet).
            const customersFromPlan = Array.isArray(planData.existingCustomers)
                ? planData.existingCustomers
                : null;
            if (customersFromPlan) {
                console.log(`[ImportWizardV2] picker seeded with ${customersFromPlan.length} customers from plan response`,
                    customersFromPlan.length > 0 ? `(first: "${customersFromPlan[0].name}")` : '');
                setExistingCustomers(customersFromPlan);
            } else {
                console.log('[ImportWizardV2] plan response did not include customers — falling back to client-side load. Did you redeploy importPlanInvoices?');
            }
            setInvPlan(planData);
            setGroupDecisions({});
            setInvoiceOverrides({});
            setExpandedGroups({});
            setPhase('inv_plan_review');
        } catch (err) {
            console.error('ImportWizardV2: invoice plan call failed', err);
            const message = err?.code === 'permission-denied'
                ? 'Only Owners, Admins, and Managers can import invoices.'
                : (err?.message || 'Plan failed. Please try again.');
            setInvPlanError(message);
            setPhase('completed');
        }
    };

    // From plan_review, advance to either unresolved-groups (if any) or
    // straight to executing.
    const handleContinueFromInvPlan = () => {
        const groups = invPlan?.unresolvedGroups || [];
        if (groups.length === 0) {
            handleStartInvoiceExecute();
        } else {
            setPhase('inv_unresolved');
        }
    };

    const handleGroupDecision = (qbCustomerName, decision) => {
        setGroupDecisions(prev => ({ ...prev, [qbCustomerName]: decision }));
    };

    const handleGroupReset = (qbCustomerName) => {
        setGroupDecisions(prev => {
            const next = { ...prev };
            delete next[qbCustomerName];
            return next;
        });
    };

    const handleToggleGroupExpanded = (qbCustomerName) => {
        setExpandedGroups(prev => ({ ...prev, [qbCustomerName]: !prev[qbCustomerName] }));
    };

    const handleInvoiceOverride = (qbCustomerName, rowIndex, override) => {
        setInvoiceOverrides(prev => {
            const groupOverrides = { ...(prev[qbCustomerName] || {}) };
            if (override === null) {
                delete groupOverrides[rowIndex];
            } else {
                groupOverrides[rowIndex] = override;
            }
            return { ...prev, [qbCustomerName]: groupOverrides };
        });
    };

    // Build manualAssignments payload from groupDecisions for the CF.
    const buildManualAssignments = () => {
        const assignments = [];
        const groups = invPlan?.unresolvedGroups || [];
        for (const g of groups) {
            const decision = groupDecisions[g.qbCustomerName];
            if (!decision) continue; // shouldn't happen — Continue gate prevents
            if (decision.action === 'skip') {
                assignments.push({ qbCustomerName: g.qbCustomerName, action: 'skip' });
            } else if (decision.action === 'assign' && decision.customerId) {
                assignments.push({
                    qbCustomerName: g.qbCustomerName,
                    action: 'assign',
                    customerId: decision.customerId,
                });
            }
        }
        return assignments;
    };

    const handleStartInvoiceExecute = async () => {
        setInvExecuteError(null);
        setPhase('inv_executing');
        setInvExecuteStartTime(Date.now());
        invCancelRequestedRef.current = false;

        const manualAssignments = buildManualAssignments();

        try {
            const callable = httpsCallable(functions, 'importExecuteInvoices');
            let chunkIndex = 0;
            let moreChunks = true;
            // Hard cap: 100 chunks. invoiceChunks(~4) + paymentChunks(~4) for
            // Michael's data sits well under that. Safety margin only.
            while (moreChunks && chunkIndex < 100 && !invCancelRequestedRef.current) {
                const payload = { importId, chunkIndex };
                if (chunkIndex === 0) payload.manualAssignments = manualAssignments;
                // eslint-disable-next-line no-await-in-loop
                const r = await callable(payload);
                const d = r.data || {};
                moreChunks = !!d.moreChunks;
                chunkIndex = d.chunksCompleted || (chunkIndex + 1);
                if (d.status === 'invoices_cancelled') {
                    setPhase('inv_cancelled');
                    return;
                }
                if (d.status === 'invoices_failed') {
                    setInvExecuteError('Invoice import failed during execution.');
                    setPhase('inv_failed');
                    return;
                }
            }
            // Mirror customer flow: if listener races, force phase transition.
            if (invCancelRequestedRef.current) {
                setTimeout(() => {
                    setPhase(p => (p === 'inv_executing' ? 'inv_cancelled' : p));
                }, 500);
            } else if (!moreChunks) {
                setTimeout(() => {
                    setPhase(p => (p === 'inv_executing' ? 'inv_completed' : p));
                }, 500);
            }
        } catch (err) {
            console.error('ImportWizardV2: invoice execute call failed', err);
            const message = err?.message || 'Invoice import failed. Please try again.';
            setInvExecuteError(message);
            setPhase('inv_failed');
        }
    };

    const handleCancelInvoiceExecute = async () => {
        if (invCancelInFlight) return;
        if (!window.confirm('Cancel the invoice import? Any invoices already imported will remain. Imports already in progress will finish their current batch before stopping.')) return;
        setInvCancelInFlight(true);
        invCancelRequestedRef.current = true;
        try {
            await updateDoc(
                doc(db, `organizations/${orgId}/imports/${importId}`),
                { cancelRequested: true, status: 'invoices_cancelled' }
            );
            setTimeout(() => {
                setPhase(p => (p === 'inv_executing' ? 'inv_cancelled' : p));
            }, 1500);
        } catch (err) {
            console.error('ImportWizardV2: invoice cancel write failed', err);
            setInvExecuteError('Could not signal cancel to the server. Local cancel applied.');
            setPhase('inv_cancelled');
        } finally {
            setInvCancelInFlight(false);
        }
    };

    // Skip-invoice-import — exits the wizard back to the success state for
    // customer flow. Does NOT touch the import doc; user can come back later.
    // (Resume-from-completed is a future enhancement; for now they'd have to
    // start a fresh customer import to get back to invoices, which is
    // acceptable since invoice import is a one-shot migration step.)
    const handleSkipInvoiceImport = () => {
        navigate('/customers');
    };

    // ROUND 4b VERIFIER: 1761900000-round4b-deals-wizard
    // ----- Jobs & follow-ups flow handlers -----

    // Called from CompletedPhase OR InvCompletedPhase via the
    // "Continue to jobs & follow-ups" CTA. Initiates importPlanDeals and
    // transitions through deal_planning -> deal_plan_review.
    const handleStartDealImport = async () => {
        // ROUND 4b-fix VERIFIER: 1761970000-round4b-error-recovery
        // Capture the phase the user was on BEFORE we transition to
        // deal_planning. If the plan call fails, we return them here rather
        // than guessing 'completed' (which was wrong if they came from
        // inv_completed). Both 'completed' and 'inv_completed' have working
        // CTAs that surface dealPlanError to the user.
        const callerPhase = (phase === 'inv_completed') ? 'inv_completed' : 'completed';
        setDealPlanError(null);
        setPhase('deal_planning');
        try {
            const callable = httpsCallable(functions, 'importPlanDeals');
            const r = await callable({ importId });
            const planSummary = r.data?.planSummary || null;
            const unresolvedGroups = r.data?.unresolvedGroups || [];
            const parseErrors = r.data?.parseErrors || [];
            if (!planSummary) {
                setDealPlanError('Plan response was empty. Please try again.');
                setPhase(p => (p === 'deal_planning' ? callerPhase : p));
                return;
            }
            setDealPlan({
                summary: planSummary,
                unresolvedGroups,
                parseErrors,
            });
            // ROUND 5d-fix VERIFIER: 1762082000-round5d-deal-picker-customers-in-plan
            // Seed picker customer list from plan response (backend includes
            // it in r.data.existingCustomers as of Round 5d-fix). Mirror of
            // the invoice flow's pattern from Round 3b's customers-in-plan-
            // fix2. The client-side fallback loader was failing to fire
            // reliably for the deal flow, leaving the picker stuck at
            // "Loading customers...". This eliminates the client-side load
            // entirely. The fallback useEffect remains for cases where the
            // backend response is missing this field (e.g., functions not
            // yet redeployed).
            const customersFromPlan = Array.isArray(r.data?.existingCustomers)
                ? r.data.existingCustomers
                : null;
            if (customersFromPlan) {
                console.log(`[ImportWizardV2] deal picker seeded with ${customersFromPlan.length} customers from plan response`,
                    customersFromPlan.length > 0 ? `(first: "${customersFromPlan[0].name}")` : '');
                setExistingCustomers(customersFromPlan);
            } else {
                console.log('[ImportWizardV2] deal plan response did not include customers — falling back to client-side load. Did you redeploy importPlanDeals?');
            }
            setDealGroupDecisions({});
            setDealExpandedGroups({});
            setPhase('deal_plan_review');
        } catch (err) {
            console.error('ImportWizardV2: deal plan call failed', err);
            const message = err?.code === 'permission-denied'
                ? 'Only Owners, Admins, and Managers can import jobs & follow-ups.'
                : (err?.message || 'Plan failed. Please try again.');
            setDealPlanError(message);
            // ROUND 4b-fix VERIFIER: 1761970000-round4b-error-recovery
            // Return to the phase the user came from (captured at call start),
            // not always 'completed' which was wrong when they came from
            // inv_completed.
            setPhase(callerPhase);
        }
    };

    // From deal_plan_review, advance to either unresolved-groups (if any) or
    // straight to executing.
    const handleContinueFromDealPlan = () => {
        const groups = dealPlan?.unresolvedGroups || [];
        if (groups.length === 0) {
            handleStartDealExecute();
        } else {
            setPhase('deal_unresolved');
        }
    };

    const handleDealGroupDecision = (orgName, decision) => {
        setDealGroupDecisions(prev => ({ ...prev, [orgName]: decision }));
    };

    const handleDealGroupReset = (orgName) => {
        setDealGroupDecisions(prev => {
            const next = { ...prev };
            delete next[orgName];
            return next;
        });
    };

    const handleToggleDealGroupExpanded = (orgName) => {
        setDealExpandedGroups(prev => ({ ...prev, [orgName]: !prev[orgName] }));
    };

    // Build manualAssignments payload from dealGroupDecisions for the CF.
    const buildDealManualAssignments = () => {
        const assignments = [];
        const groups = dealPlan?.unresolvedGroups || [];
        for (const g of groups) {
            const decision = dealGroupDecisions[g.orgName];
            if (!decision) continue; // shouldn't happen — Continue gate prevents
            if (decision.action === 'skip') {
                assignments.push({ orgName: g.orgName, action: 'skip' });
            } else if (decision.action === 'assign' && decision.customerId) {
                assignments.push({
                    orgName: g.orgName,
                    action: 'assign',
                    customerId: decision.customerId,
                });
            }
        }
        return assignments;
    };

    const handleStartDealExecute = async () => {
        setDealExecuteError(null);
        setPhase('deal_executing');
        setDealExecuteStartTime(Date.now());
        dealCancelRequestedRef.current = false;

        const manualAssignments = buildDealManualAssignments();

        try {
            const callable = httpsCallable(functions, 'importExecuteDeals');
            let chunkIndex = 0;
            let moreChunks = true;
            // Hard cap: 200 chunks. Round 4 may have more chunks than Round 3
            // because of the three-phase split (parents + follow-ups + audit).
            // For Michael's data: ~2 parent chunks + ~1 follow-up chunk + ~7
            // audit chunks = ~10 total. Cap at 200 is generous safety margin.
            while (moreChunks && chunkIndex < 200 && !dealCancelRequestedRef.current) {
                const payload = { importId, chunkIndex };
                if (chunkIndex === 0) {
                    payload.manualAssignments = manualAssignments;
                    payload.skipAnomalies = skipDealAnomalies;
                }
                // eslint-disable-next-line no-await-in-loop
                const r = await callable(payload);
                const d = r.data || {};
                moreChunks = !!d.moreChunks;
                chunkIndex = d.chunksCompleted || (chunkIndex + 1);
                if (d.status === 'deals_cancelled') {
                    setPhase('deal_cancelled');
                    return;
                }
                if (d.status === 'deals_failed') {
                    setDealExecuteError('Jobs & follow-ups import failed during execution.');
                    setPhase('deal_failed');
                    return;
                }
            }
            // Mirror invoice flow: if listener races, force phase transition.
            if (dealCancelRequestedRef.current) {
                setTimeout(() => {
                    setPhase(p => (p === 'deal_executing' ? 'deal_cancelled' : p));
                }, 500);
            } else if (!moreChunks) {
                setTimeout(() => {
                    setPhase(p => (p === 'deal_executing' ? 'deal_completed' : p));
                }, 500);
            }
        } catch (err) {
            console.error('ImportWizardV2: deal execute call failed', err);
            const message = err?.message || 'Jobs & follow-ups import failed. Please try again.';
            setDealExecuteError(message);
            setPhase('deal_failed');
        }
    };

    const handleCancelDealExecute = async () => {
        if (dealCancelInFlight) return;
        if (!window.confirm('Cancel the import? Any jobs & follow-ups already imported will remain. Imports already in progress will finish their current batch before stopping.')) return;
        setDealCancelInFlight(true);
        dealCancelRequestedRef.current = true;
        try {
            await updateDoc(
                doc(db, `organizations/${orgId}/imports/${importId}`),
                { cancelRequested: true, status: 'deals_cancelled' }
            );
            setTimeout(() => {
                setPhase(p => (p === 'deal_executing' ? 'deal_cancelled' : p));
            }, 1500);
        } catch (err) {
            console.error('ImportWizardV2: deal cancel write failed', err);
            setDealExecuteError('Could not signal cancel to the server. Local cancel applied.');
        } finally {
            setDealCancelInFlight(false);
        }
    };

    // Skip-deal-import — exits the wizard back to customers list.
    const handleSkipDealImport = () => {
        navigate('/customers');
    };

    // --- Handlers: Navigate to customers after success ---
    const handleGoToCustomers = () => navigate('/customers');

    // --- Render: permission gate ---
    if (!isAuthorized) {
        return (
            <div className="p-8">
                <div className="max-w-md mx-auto bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                    <Lock size={32} className="mx-auto text-amber-600 mb-3" />
                    <h2 className="text-lg font-semibold text-amber-800">Insufficient Permissions</h2>
                    <p className="text-sm text-amber-700 mt-2">
                        Only Owners, Admins, and Managers can use the data import wizard.
                    </p>
                </div>
            </div>
        );
    }

    // --- Render: main ---
    return (
        <div className="p-6 sm:p-8">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Data Import</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Bring your customer, invoice, and deal data into Spatchy from QuickBooks, Pipedrive, or any spreadsheet.
                </p>
            </div>

            {/* Phase indicator */}
            <PhaseIndicator phase={phase} />

            {/* === PHASE 1: UPLOAD === */}
            {phase === 'upload' && (
                <UploadPhase
                    uploads={uploads}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClickBrowse={handleBrowseClick}
                    onInputChange={handleInputChange}
                    onRemove={removeUpload}
                    isDragActive={isDragActive}
                    detectError={detectError}
                    canDetect={canDetect}
                    onDetect={handleDetect}
                    fileInputRef={fileInputRef}
                />
            )}

            {/* === PHASE 2: DETECTING === */}
            {phase === 'detecting' && (
                <SpinnerPhase
                    title="Analyzing your files…"
                    subtitle="This usually takes 5–15 seconds for typical exports."
                />
            )}

            {/* === PHASE 3: REVIEW === */}
            {phase === 'review' && (
                <ReviewPhase
                    detectionFiles={detectionFiles}
                    userMappings={userMappings}
                    onMappingChange={handleMappingChange}
                    expandedSamples={expandedSamples}
                    toggleSampleExpanded={toggleSampleExpanded}
                    isFileReady={isFileReady}
                    allFilesReady={allFilesReady}
                    onStartOver={handleStartOver}
                    onContinue={handleContinueToPlan}
                    onImportLineItems={handlePlanLineItems}
                    planError={planError}
                />
            )}

            {/* === PHASE 4a: PLANNING === */}
            {phase === 'planning' && (
                <SpinnerPhase
                    title="Building your import plan…"
                    subtitle="We're analyzing your data to identify duplicates and prepare the import."
                />
            )}

            {/* === PHASE 4b: PLAN REVIEW === */}
            {phase === 'plan_review' && plan && (
                <PlanReviewPhase
                    plan={plan}
                    ambiguousDecisions={ambiguousDecisions}
                    onAmbiguousDecision={handleAmbiguousDecision}
                    onAmbiguousReset={handleAmbiguousReset}
                    decidedCount={decidedCount}
                    skippedCount={skippedCount}
                    onCancel={handleStartOver}
                    onStartImport={handleStartImport}
                    executeError={executeError}
                    existingCustomers={existingCustomers}
                />
            )}

            {/* === PHASE 5: EXECUTING === */}
            {phase === 'executing' && (
                <ExecutePhase
                    progress={executeProgress}
                    plan={plan}
                    startTime={executeStartTime}
                    onCancel={handleCancelExecute}
                    cancelInFlight={cancelInFlight}
                />
            )}

            {/* === PHASE 6a: COMPLETED === */}
            {phase === 'completed' && (
                <CompletedPhase
                    progress={executeProgress}
                    detectionFiles={detectionFiles}
                    invPlanError={invPlanError}
                    dealPlanError={dealPlanError}
                    onContinueToInvoices={handleStartInvoiceImport}
                    onContinueToDeals={handleStartDealImport}
                    onGoToCustomers={handleGoToCustomers}
                    onStartOver={handleStartOver}
                />
            )}

            {/* === PHASE 6b: CANCELLED === */}
            {phase === 'cancelled' && (
                <CancelledPhase
                    progress={executeProgress}
                    onGoToCustomers={handleGoToCustomers}
                    onStartOver={handleStartOver}
                />
            )}

            {/* === PHASE 6c: FAILED === */}
            {phase === 'failed' && (
                <FailedPhase
                    error={executeError}
                    progress={executeProgress}
                    onGoToCustomers={handleGoToCustomers}
                    onStartOver={handleStartOver}
                />
            )}

            {/* === LINE ITEMS (Products & Services) === [seam e7b1d4c9] */}
            {phase === 'li_planning' && (
                <SpinnerPhase
                    title="Reading your Products & Services…"
                    subtitle="We're checking each item and preparing the import."
                />
            )}

            {phase === 'li_review' && liPlan && (
                <LineItemReviewPhase
                    plan={liPlan}
                    imports={liImport}
                    onToggle={handleLiToggle}
                    onCancel={handleStartOver}
                    onStartImport={handleStartLineItemImport}
                    executeError={liExecuteError}
                />
            )}

            {phase === 'li_executing' && (
                <LineItemExecutePhase
                    progress={liExecuteProgress}
                    plan={liPlan}
                    startTime={liExecuteStartTime}
                    onCancel={handleCancelLineItemImport}
                    cancelInFlight={liCancelInFlight}
                />
            )}

            {phase === 'li_completed' && (
                <LineItemCompletedPhase
                    progress={liExecuteProgress}
                    onStartOver={handleStartOver}
                />
            )}

            {phase === 'li_cancelled' && (
                <LineItemCancelledPhase
                    progress={liExecuteProgress}
                    onStartOver={handleStartOver}
                />
            )}

            {phase === 'li_failed' && (
                <FailedPhase
                    error={liExecuteError}
                    progress={liExecuteProgress}
                    onStartOver={handleStartOver}
                />
            )}

            {/* ROUND 3b VERIFIER: 1761744000-round3b-render-gates */}
            {/* === PHASE 10: INVOICE PLANNING === */}
            {phase === 'inv_planning' && (
                <SpinnerPhase
                    title="Building your invoice import plan…"
                    subtitle="We're analyzing your QuickBooks Sales data."
                />
            )}

            {/* === PHASE 11: INVOICE PLAN REVIEW === */}
            {phase === 'inv_plan_review' && invPlan && (
                <InvPlanReviewPhase
                    plan={invPlan}
                    onSkip={handleSkipInvoiceImport}
                    onContinue={handleContinueFromInvPlan}
                    invExecuteError={invExecuteError}
                />
            )}

            {/* === PHASE 12: INVOICE UNRESOLVED GROUPS === */}
            {phase === 'inv_unresolved' && invPlan && (
                <InvUnresolvedGroupsPhase
                    plan={invPlan}
                    groupDecisions={groupDecisions}
                    invoiceOverrides={invoiceOverrides}
                    expandedGroups={expandedGroups}
                    existingCustomers={existingCustomers}
                    existingCustomersLoading={existingCustomersLoading}
                    onGroupDecision={handleGroupDecision}
                    onGroupReset={handleGroupReset}
                    onToggleExpanded={handleToggleGroupExpanded}
                    onInvoiceOverride={handleInvoiceOverride}
                    onBack={() => setPhase('inv_plan_review')}
                    onStartImport={handleStartInvoiceExecute}
                    invExecuteError={invExecuteError}
                />
            )}

            {/* === PHASE 13: INVOICE EXECUTING === */}
            {phase === 'inv_executing' && (
                <InvExecutePhase
                    progress={invExecuteProgress}
                    plan={invPlan}
                    startTime={invExecuteStartTime}
                    onCancel={handleCancelInvoiceExecute}
                    cancelInFlight={invCancelInFlight}
                />
            )}

            {/* === PHASE 14: INVOICE COMPLETED === */}
            {phase === 'inv_completed' && (
                <InvCompletedPhase
                    progress={invExecuteProgress}
                    detectionFiles={detectionFiles}
                    dealPlanError={dealPlanError}
                    onContinueToDeals={handleStartDealImport}
                    onGoToCustomers={handleGoToCustomers}
                    onStartOver={handleStartOver}
                />
            )}

            {/* === PHASE 15: INVOICE CANCELLED === */}
            {phase === 'inv_cancelled' && (
                <InvCancelledPhase
                    progress={invExecuteProgress}
                    onGoToCustomers={handleGoToCustomers}
                    onStartOver={handleStartOver}
                />
            )}

            {/* === PHASE 16: INVOICE FAILED === */}
            {phase === 'inv_failed' && (
                <InvFailedPhase
                    error={invExecuteError}
                    progress={invExecuteProgress}
                    onGoToCustomers={handleGoToCustomers}
                    onStartOver={handleStartOver}
                />
            )}

            {/* ROUND 4b VERIFIER: 1761900000-round4b-deals-wizard */}
            {/* === PHASE 20: DEAL PLANNING === */}
            {phase === 'deal_planning' && (
                <SpinnerPhase
                    title="Building your jobs & follow-ups import plan…"
                    subtitle="We're analyzing your Pipedrive data."
                />
            )}

            {/* === PHASE 21: DEAL PLAN REVIEW === */}
            {phase === 'deal_plan_review' && dealPlan && (
                <DealPlanReviewPhase
                    plan={dealPlan}
                    skipAnomalies={skipDealAnomalies}
                    onToggleSkipAnomalies={() => setSkipDealAnomalies(v => !v)}
                    onSkip={handleSkipDealImport}
                    onContinue={handleContinueFromDealPlan}
                    dealExecuteError={dealExecuteError}
                />
            )}

            {/* === PHASE 22: DEAL UNRESOLVED GROUPS === */}
            {phase === 'deal_unresolved' && dealPlan && (
                <DealUnresolvedGroupsPhase
                    plan={dealPlan}
                    groupDecisions={dealGroupDecisions}
                    expandedGroups={dealExpandedGroups}
                    existingCustomers={existingCustomers}
                    existingCustomersLoading={existingCustomersLoading}
                    onGroupDecision={handleDealGroupDecision}
                    onGroupReset={handleDealGroupReset}
                    onToggleExpanded={handleToggleDealGroupExpanded}
                    onBack={() => setPhase('deal_plan_review')}
                    onStartImport={handleStartDealExecute}
                    dealExecuteError={dealExecuteError}
                />
            )}

            {/* === PHASE 23: DEAL EXECUTING === */}
            {phase === 'deal_executing' && (
                <DealExecutePhase
                    progress={dealExecuteProgress}
                    plan={dealPlan}
                    startTime={dealExecuteStartTime}
                    onCancel={handleCancelDealExecute}
                    cancelInFlight={dealCancelInFlight}
                />
            )}

            {/* === PHASE 24: DEAL COMPLETED === */}
            {phase === 'deal_completed' && (
                <DealCompletedPhase
                    progress={dealExecuteProgress}
                    onGoToCustomers={handleGoToCustomers}
                    onStartOver={handleStartOver}
                />
            )}

            {/* === PHASE 25: DEAL CANCELLED === */}
            {phase === 'deal_cancelled' && (
                <DealCancelledPhase
                    progress={dealExecuteProgress}
                    onGoToCustomers={handleGoToCustomers}
                    onStartOver={handleStartOver}
                />
            )}

            {/* === PHASE 26: DEAL FAILED === */}
            {phase === 'deal_failed' && (
                <DealFailedPhase
                    error={dealExecuteError}
                    progress={dealExecuteProgress}
                    onGoToCustomers={handleGoToCustomers}
                    onStartOver={handleStartOver}
                />
            )}
        </div>
    );
};

// ============================================================================
// Sub-components
// ============================================================================

const PhaseIndicator = ({ phase }) => {
    // ROUND 3b VERIFIER: 1761744000-round3b-phase-indicator
    // Three indicator strips depending on which sub-flow the user is in.
    // Customer flow uses the original 6-step strip. Invoice flow uses a
    // focused 4-step strip. Round 4b: deal flow uses its own 4-step strip
    // labeled "Jobs & follow-ups import:" to keep user-facing language
    // Spatchy-native rather than source-system-flavored.
    const isInvoiceFlow = phase && phase.startsWith('inv_');
    const isDealFlow = phase && phase.startsWith('deal_');

    const pillForStep = (label, isActive, isComplete, num) => (
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${isActive ? 'bg-indigo-100 text-indigo-700 font-semibold' : isComplete ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${isActive ? 'bg-indigo-600 text-white' : isComplete ? 'bg-emerald-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                {isComplete ? '✓' : num}
            </span>
            {label}
        </div>
    );

    // ROUND 4b VERIFIER: 1761900000-round4b-deals-wizard
    if (isDealFlow) {
        const stepOf = (p) => {
            if (p === 'deal_planning') return 0;
            if (p === 'deal_plan_review') return 0;
            if (p === 'deal_unresolved') return 1;
            if (p === 'deal_executing') return 2;
            if (p === 'deal_completed' || p === 'deal_cancelled' || p === 'deal_failed') return 3;
            return 0;
        };
        const step = stepOf(phase);
        return (
            <div className="flex items-center gap-2 mb-8 text-xs flex-wrap">
                <div className="text-[11px] text-gray-500 font-medium mr-1">Jobs &amp; follow-ups import:</div>
                {pillForStep('Review plan', step === 0, step > 0, 1)}
                <div className="text-gray-300">›</div>
                {pillForStep('Resolve unmatched', step === 1, step > 1, 2)}
                <div className="text-gray-300">›</div>
                {pillForStep('Import', step === 2, step > 2, 3)}
                <div className="text-gray-300">›</div>
                {pillForStep('Done', step === 3, false, 4)}
            </div>
        );
    }

    if (isInvoiceFlow) {
        // 4-step strip: Review plan → Resolve unmatched → Import → Done
        const stepOf = (p) => {
            if (p === 'inv_planning') return 0;
            if (p === 'inv_plan_review') return 0;
            if (p === 'inv_unresolved') return 1;
            if (p === 'inv_executing') return 2;
            if (p === 'inv_completed' || p === 'inv_cancelled' || p === 'inv_failed') return 3;
            return 0;
        };
        const step = stepOf(phase);
        return (
            <div className="flex items-center gap-2 mb-8 text-xs flex-wrap">
                <div className="text-[11px] text-gray-500 font-medium mr-1">Invoice import:</div>
                {pillForStep('Review plan', step === 0, step > 0, 1)}
                <div className="text-gray-300">›</div>
                {pillForStep('Resolve unmatched', step === 1, step > 1, 2)}
                <div className="text-gray-300">›</div>
                {pillForStep('Import', step === 2, step > 2, 3)}
                <div className="text-gray-300">›</div>
                {pillForStep('Done', step === 3, false, 4)}
            </div>
        );
    }

    const stepOf = (p) => {
        if (p === 'upload') return 0;
        if (p === 'detecting') return 1;
        if (p === 'review') return 2;
        if (p === 'planning') return 3;
        if (p === 'plan_review') return 3;
        if (p === 'executing') return 4;
        if (p === 'completed' || p === 'cancelled' || p === 'failed') return 5;
        return 0;
    };
    const step = stepOf(phase);
    return (
        <div className="flex items-center gap-2 mb-8 text-xs flex-wrap">
            {pillForStep('Upload', step === 0, step > 0, 1)}
            <div className="text-gray-300">›</div>
            {pillForStep('Detect', step === 1, step > 1, 2)}
            <div className="text-gray-300">›</div>
            {pillForStep('Review files', step === 2, step > 2, 3)}
            <div className="text-gray-300">›</div>
            {pillForStep('Review plan', step === 3, step > 3, 4)}
            <div className="text-gray-300">›</div>
            {pillForStep('Import', step === 4, step > 4, 5)}
            <div className="text-gray-300">›</div>
            {pillForStep('Done', step === 5, false, 6)}
        </div>
    );
};

const SpinnerPhase = ({ title, subtitle }) => (
    <div className="text-center py-16">
        <Loader2 size={36} className="mx-auto text-indigo-500 animate-spin mb-4" />
        <p className="text-base font-medium text-gray-700">{title}</p>
        {subtitle && <p className="text-sm text-gray-500 mt-2">{subtitle}</p>}
    </div>
);

const UploadPhase = ({
    uploads, onDrop, onDragOver, onDragLeave, onClickBrowse, onInputChange,
    onRemove, isDragActive, detectError, canDetect, onDetect, fileInputRef,
}) => (
    <div>
        <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={onClickBrowse}
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${
                isDragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
            }`}
        >
            <UploadCloud size={36} className="mx-auto text-gray-400 mb-3" />
            <p className="text-sm font-medium text-gray-700">
                Drop files here or <span className="text-indigo-600 underline">browse</span>
            </p>
            <p className="text-xs text-gray-500 mt-2">
                Excel (.xls, .xlsx) or CSV. Up to {MAX_FILES} files, 50MB each.<br/>
                Files are private to your organization and auto-delete after 30 days.
            </p>
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".xls,.xlsx,.csv"
                className="hidden"
                onChange={onInputChange}
            />
        </div>

        {uploads.length > 0 && (
            <div className="mt-6 space-y-2">
                {uploads.map(u => (
                    <div
                        key={u.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border ${
                            u.status === 'failed' ? 'bg-red-50 border-red-200' :
                            u.status === 'done'   ? 'bg-emerald-50 border-emerald-200' :
                                                    'bg-gray-50 border-gray-200'
                        }`}
                    >
                        <FileSpreadsheet size={20} className={
                            u.status === 'failed' ? 'text-red-500' :
                            u.status === 'done'   ? 'text-emerald-600' :
                                                    'text-gray-400'
                        } />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-800 truncate">{u.file.name}</span>
                                <span className="text-xs text-gray-500 ml-2">{formatBytes(u.file.size)}</span>
                            </div>
                            {u.status === 'uploading' && (
                                <div className="mt-1.5 h-1 bg-gray-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-indigo-500 transition-all" style={{ width: `${u.progress}%` }} />
                                </div>
                            )}
                            {u.status === 'failed' && u.error && (
                                <p className="text-xs text-red-600 mt-1">{u.error}</p>
                            )}
                        </div>
                        {u.status === 'done' && <CheckCircle2 size={18} className="text-emerald-600" />}
                        {u.status === 'failed' && <AlertOctagon size={18} className="text-red-500" />}
                        {u.status === 'uploading' && <Loader2 size={18} className="text-indigo-500 animate-spin" />}
                        <button
                            onClick={(e) => { e.stopPropagation(); onRemove(u.id); }}
                            className="text-gray-400 hover:text-gray-600 p-1"
                            aria-label="Remove file"
                        ><X size={16} /></button>
                    </div>
                ))}
            </div>
        )}

        {detectError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {detectError}
            </div>
        )}

        {uploads.length > 0 && (
            <div className="mt-6 flex justify-end">
                <button
                    onClick={onDetect}
                    disabled={!canDetect}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                        canDetect ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                >
                    Detect file types
                    <ArrowRight size={16} />
                </button>
            </div>
        )}
    </div>
);

const ReviewPhase = ({
    detectionFiles, userMappings, onMappingChange, expandedSamples,
    toggleSampleExpanded, isFileReady, allFilesReady, onStartOver, onContinue, onImportLineItems, planError,
}) => (
    <div>
        <p className="text-sm text-gray-600 mb-4">
            Review what was detected. Files marked as "unknown" need columns mapped manually.
        </p>

        <div className="space-y-3">
            {detectionFiles.map(f => (
                <DetectedFileCard
                    key={f.filename}
                    file={f}
                    userMapping={userMappings[f.filename] || {}}
                    onMappingChange={onMappingChange}
                    isExpanded={!!expandedSamples[f.filename]}
                    onToggleExpand={() => toggleSampleExpanded(f.filename)}
                    isReady={isFileReady(f)}
                />
            ))}
        </div>

        {planError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {planError}
            </div>
        )}

        <div className="mt-8 flex items-center justify-between gap-4 pt-4 border-t border-gray-200">
            <button
                onClick={onStartOver}
                className="text-sm font-medium text-gray-600 hover:text-gray-800"
            >
                ← Start over
            </button>
            {/* line-items hub entry [seam e7b1d4c9] */}
            <div className="flex items-center gap-3">
                {detectionFiles.some(f => f.mappingMode !== 'failed' && f.entityType === 'line_items') && (
                    <button
                        disabled={!allFilesReady}
                        onClick={onImportLineItems}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                            allFilesReady ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                    >
                        Import Products &amp; Services
                        <ArrowRight size={16} />
                    </button>
                )}
                {detectionFiles.some(f => f.mappingMode !== 'failed' && f.entityType !== 'line_items') && (
                    <button
                        disabled={!allFilesReady}
                        onClick={onContinue}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                            allFilesReady ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                    >
                        Continue to review
                        <ArrowRight size={16} />
                    </button>
                )}
            </div>
        </div>
    </div>
);

const DetectedFileCard = ({ file, userMapping, onMappingChange, isExpanded, onToggleExpand, isReady }) => {
    const isAuto    = file.mappingMode === 'auto';
    const isManual  = file.mappingMode === 'manual_required';
    const isFailed  = file.mappingMode === 'failed';
    const hasPrev   = !!file.previousImport;

    const cardClasses = isFailed ? 'border-red-200 bg-red-50'
        : hasPrev ? 'border-blue-200 bg-blue-50'
        : isAuto ? 'border-emerald-200 bg-emerald-50'
        : 'border-amber-200 bg-amber-50';

    const StatusIcon = isFailed ? AlertOctagon : hasPrev ? Info : isAuto ? CheckCircle2 : AlertTriangle;
    const iconColor = isFailed ? 'text-red-500'
        : hasPrev ? 'text-blue-500'
        : isAuto ? 'text-emerald-600'
        : 'text-amber-500';

    return (
        <div className={`rounded-xl border ${cardClasses} overflow-hidden`}>
            <div className="p-4">
                <div className="flex items-start gap-3">
                    <StatusIcon size={20} className={`${iconColor} mt-0.5 flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-gray-800 truncate">{file.filename}</span>
                            {isReady && !isFailed && (
                                <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">Ready</span>
                            )}
                        </div>

                        {isAuto && (
                            <p className="text-sm text-gray-700 mt-1">
                                Detected as <span className="font-semibold">{file.sourceName}</span>
                                <span className="text-gray-500"> — {file.recordCount.toLocaleString()} records</span>
                            </p>
                        )}

                        {isManual && (
                            <p className="text-sm text-gray-700 mt-1">
                                Source not recognized — please map columns below.
                                <span className="text-gray-500"> ({file.recordCount.toLocaleString()} records)</span>
                            </p>
                        )}

                        {isFailed && file.errors && file.errors.length > 0 && (
                            <p className="text-sm text-red-700 mt-1">{file.errors[0]}</p>
                        )}

                        {hasPrev && (
                            <p className="text-xs text-blue-700 mt-2">
                                Previously imported on {formatDate(file.previousImport.date) || 'an earlier date'}
                                {file.previousImport.recordsImported > 0 ? ` — ${file.previousImport.recordsImported.toLocaleString()} records already in your system.` : '.'}
                                {' '}Re-uploading will skip duplicates and add only new entries.
                            </p>
                        )}
                    </div>
                </div>

                {!isFailed && file.sampleRows && file.sampleRows.length > 0 && (
                    <button
                        onClick={onToggleExpand}
                        className="mt-3 flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-800"
                    >
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        {isExpanded ? 'Hide' : 'Show'} sample data
                    </button>
                )}
            </div>

            {isExpanded && file.sampleRows && file.sampleRows.length > 0 && (
                <div className="px-4 pb-4 overflow-x-auto">
                    <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                        <table className="min-w-full text-xs">
                            <thead className="bg-gray-50">
                                <tr>
                                    {Object.keys(file.sampleRows[0] || {}).map(col => (
                                        <th key={col} className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">{col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {file.sampleRows.map((row, i) => (
                                    <tr key={i} className="border-t border-gray-100">
                                        {Object.keys(file.sampleRows[0] || {}).map(col => (
                                            <td key={col} className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-[200px] truncate">
                                                {String(row[col] ?? '')}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {isManual && (
                <div className="bg-white border-t border-amber-200 p-4">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                        Map your columns to Spatchy fields
                    </p>
                    <p className="text-xs text-gray-500 mb-3">
                        At least one of First Name, Last Name, Company, Email, or Phone must be mapped.
                    </p>
                    <div className="space-y-2">
                        {(file.detectedColumns || []).map(col => (
                            <div key={col} className="flex items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <span className="text-sm text-gray-700 truncate block">{col}</span>
                                </div>
                                <ArrowRight size={14} className="text-gray-300 flex-shrink-0" />
                                <select
                                    value={userMapping[col] || ''}
                                    onChange={(e) => onMappingChange(file.filename, col, e.target.value)}
                                    className="flex-1 text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                >
                                    <option value="">— skip this column —</option>
                                    {MAPPABLE_FIELDS.map(f => (
                                        <option key={f.fieldId} value={f.fieldId}>{f.label}</option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const PlanReviewPhase = ({
    plan, ambiguousDecisions, onAmbiguousDecision, onAmbiguousReset,
    decidedCount, skippedCount, onCancel, onStartImport, executeError,
    existingCustomers,
}) => {
    const summary = plan.summary || {};
    // ROUND 6.5 VERIFIER: 1762110000-round6_5-per-candidate-review
    // Per-candidate review surface. Backend sends `ambiguousCandidates` —
    // one entry per unique candidate involved in any ambiguous pair, with
    // the others it was paired with as `suggestions`.
    const ambiguousCandidates = plan.ambiguousCandidates || [];

    // ROUND 6.5-fix2 VERIFIER: 1762120000-round6_5-implicit-decisions
    // When candidate A's decision merges with candidate B (via merge_with_suggestions
    // or match_to_candidate), B's card gets hidden from the review list. The
    // single decision resolves both cards; showing both creates redundant
    // (and confusing) UI where the user is asked the same merge question twice.
    const implicitlyDecidedSet = useMemo(() => {
        const set = new Set();
        for (const v of Object.values(ambiguousDecisions || {})) {
            if (!v || typeof v !== 'object') continue;
            if (v.type === 'merge_with_suggestions' && Array.isArray(v.candidateSuggestionIndices)) {
                for (const idx of v.candidateSuggestionIndices) set.add(idx);
            } else if (v.type === 'match_to_candidate' && typeof v.targetCandidateIndex === 'number') {
                set.add(v.targetCandidateIndex);
            }
        }
        return set;
    }, [ambiguousDecisions]);

    const visibleCandidates = useMemo(() => {
        return ambiguousCandidates.filter(c => {
            // Hide if implicitly decided (referenced by another's decision)
            // AND has no explicit decision of its own. If it has its own
            // explicit decision (e.g., create_new), keep it visible as a
            // decided card so the user can review/change it.
            if (implicitlyDecidedSet.has(c.candidateIndex)) {
                const ownDecision = ambiguousDecisions[c.candidateIndex];
                if (!ownDecision || typeof ownDecision !== 'object') return false;
            }
            return true;
        });
    }, [ambiguousCandidates, ambiguousDecisions, implicitlyDecidedSet]);

    const hasAmbiguous = ambiguousCandidates.length > 0;

    return (
        <div>
            {/* Section 1: Plan summary */}
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5 mb-5">
                <h3 className="text-base font-semibold text-indigo-900 flex items-center gap-2">
                    <Users size={18} /> Ready to import.
                </h3>
                <div className="mt-3 space-y-2 text-sm text-gray-800">
                    <SummaryLine
                        icon={<Users size={14} className="text-indigo-600" />}
                        label="Customers"
                        value={
                            <>
                                <span className="font-bold text-indigo-700">{summary.toCreate || 0}</span> new
                                {(summary.toUpdate || 0) > 0 && (
                                    <>
                                        {' · '}
                                        <span className="font-bold text-indigo-700">{summary.toUpdate}</span> existing will be updated
                                    </>
                                )}
                            </>
                        }
                    />
                    {(summary.openingInvoicesCount || 0) > 0 && (
                        <SummaryLine
                            icon={<Receipt size={14} className="text-indigo-600" />}
                            label="Opening invoices"
                            value={
                                <>
                                    <span className="font-bold text-indigo-700">{summary.openingInvoicesCount}</span>
                                    {' totaling '}
                                    <span className="font-bold text-indigo-700">{formatCurrency(summary.openingInvoicesTotal || 0)}</span>
                                </>
                            }
                        />
                    )}
                    {(summary.autoMergeLevel1 + summary.autoMergeLevel2 + summary.autoMergeLevel3Fuzzy) > 0 && (
                        <SummaryLine
                            icon={<ArrowLeftRight size={14} className="text-indigo-600" />}
                            label="Auto-merged duplicates"
                            value={
                                <span className="text-gray-700">
                                    {summary.autoMergeLevel1 + summary.autoMergeLevel2 + summary.autoMergeLevel3Fuzzy} groups
                                    {' '}
                                    <span className="text-gray-500">
                                        ({summary.autoMergeLevel1} verified · {summary.autoMergeLevel2} exact name · {summary.autoMergeLevel3Fuzzy} fuzzy)
                                    </span>
                                </span>
                            }
                        />
                    )}
                </div>
            </div>

            {/* Section 2: Ambiguous matches — Round 6.5 per-candidate review */}
            {hasAmbiguous && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-5">
                    <div className="flex items-start gap-3 mb-3">
                        <AlertTriangle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                            <h3 className="text-base font-semibold text-amber-900">
                                {ambiguousCandidates.length} customer{ambiguousCandidates.length === 1 ? '' : 's'} need{ambiguousCandidates.length === 1 ? 's' : ''} your review
                            </h3>
                            <p className="text-xs text-amber-700 mt-1">
                                We found possible duplicates for these. For each one, decide whether it's the same as another customer or should be created as new.
                            </p>
                        </div>
                        <div className="ml-auto text-xs font-medium text-amber-800 whitespace-nowrap">
                            {decidedCount} of {ambiguousCandidates.length} decided
                        </div>
                    </div>

                    <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                        {visibleCandidates.map((c) => (
                            <CandidateReviewCard
                                key={c.candidateIndex}
                                candidate={c}
                                decision={ambiguousDecisions[c.candidateIndex]}
                                onDecision={onAmbiguousDecision}
                                onReset={onAmbiguousReset}
                                existingCustomers={existingCustomers || []}
                                allCandidates={ambiguousCandidates}
                            />
                        ))}
                    </div>

                    {skippedCount > 0 && (
                        <p className="text-xs text-amber-800 mt-3 italic">
                            Note: {skippedCount} undecided customer{skippedCount === 1 ? '' : 's'} will be created as new. Decide them now if you want them merged with another.
                        </p>
                    )}
                </div>
            )}

            {executeError && (
                <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {executeError}
                </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between gap-4 pt-4 border-t border-gray-200">
                <button
                    onClick={onCancel}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                    ← Cancel and start over
                </button>
                <button
                    onClick={onStartImport}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                >
                    Start import
                    <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

const SummaryLine = ({ icon, label, value }) => (
    <div className="flex items-start gap-2">
        <div className="mt-0.5">{icon}</div>
        <div>
            <span className="text-gray-600">{label}: </span>
            <span>{value}</span>
        </div>
    </div>
);

// ROUND 6.5 VERIFIER: 1762110000-round6_5-per-candidate-review
// Per-candidate review card. One card per ambiguous candidate. The user
// makes a single decision per candidate:
//   - Check one or more "system found possible duplicates" checkboxes
//     (multi-select; merges all checked into one customer with this candidate)
//   - "Create as a new customer" radio (default)
//   - "Match to a different customer instead..." opens an inline search
//     with two sections: existing DB customers + other rows in this import
//
// ROUND 7 VERIFIER: 1762130000-round7-fuzzy-vs-existing-db
// Suggestions are now mixed-type: each is either kind='candidate' (another
// row in this import) or kind='existing_customer' (a customer already in
// the user's DB). Existing-customer suggestions trigger a higher-stakes
// merge — into a record that already has history. The Confirm Merge button
// rolls them all together: all checked candidates get unioned with this
// row, and if an existing customer is checked, the resulting group lands
// on that DB customer (action='update').
//
// Decision shape (frontend state, keyed by candidateIndex):
//   { type: 'create_new' }
//   { type: 'merge_with_suggestions', candidateSuggestionIndices: [42, 99],
//     existingCustomerId: 'xyz' | null, existingCustomerName: '...' | null }
//   { type: 'match_to_existing', existingCustomerId, customerName }
//   { type: 'match_to_candidate', targetCandidateIndex, targetName }
const CandidateReviewCard = ({ candidate, decision, onDecision, onReset, existingCustomers, allCandidates }) => {
    // ROUND 6.5-fix: All React hooks must be called unconditionally and in the
    // same order on every render. Previously the useMemo/useState calls below
    // the "decided" early-return violated Rules of Hooks. Hoisted to top.
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchResults, setShowSearchResults] = useState(false);
    // ROUND 7: state holds mixed types — { kind: 'candidate', candidateIndex }
    // or { kind: 'existing_customer', existingCustomerId }. Only ONE
    // existing_customer can be checked at a time (per-design — Marcus signed
    // off; matching to multiple DB records would imply merging the DB
    // records themselves, which is a separate feature).
    const [checkedSuggestions, setCheckedSuggestions] = useState([]);

    // Build "other candidates from this import" for the search bar (exclude self
    // AND exclude any candidate already shown as a suggestion to avoid noise).
    // Round 7: only consider candidate-kind suggestions for exclusion; DB-kind
    // suggestions are tracked separately and don't intersect with this list.
    const otherCandidates = useMemo(() => {
        const suggestionIndexSet = new Set(
            (candidate.suggestions || [])
                .filter(s => s.kind === 'candidate' || !s.kind)
                .map(s => s.candidateIndex)
        );
        const out = [];
        for (const c of allCandidates) {
            if (c.candidateIndex === candidate.candidateIndex) continue;
            if (suggestionIndexSet.has(c.candidateIndex)) continue; // already in suggestions
            out.push({ candidateIndex: c.candidateIndex, name: c.displayName });
        }
        out.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        return out;
    }, [candidate.candidateIndex, candidate.suggestions, allCandidates]);

    const filteredExisting = useMemo(() => {
        if (!searchQuery.trim()) return [];
        const q = searchQuery.toLowerCase();
        return (existingCustomers || [])
            .filter(c => (c.name || '').toLowerCase().includes(q))
            .slice(0, 8);
    }, [searchQuery, existingCustomers]);

    const filteredOtherCandidates = useMemo(() => {
        if (!searchQuery.trim()) return [];
        const q = searchQuery.toLowerCase();
        return otherCandidates
            .filter(c => c.name.toLowerCase().includes(q))
            .slice(0, 8);
    }, [searchQuery, otherCandidates]);

    const sourceLabel = (s) => {
        if (s === 'qb_customers') return 'QuickBooks';
        if (s === 'pipedrive_clients') return 'Pipedrive';
        if (s === 'manual_mapped') return 'Manual mapping';
        return s || 'Unknown';
    };

    const isDecided = decision && typeof decision === 'object' &&
        ['create_new', 'merge_with_suggestions', 'match_to_existing', 'match_to_candidate'].includes(decision.type);

    // ---- Decided (collapsed) state ----
    if (isDecided) {
        let icon, iconColor, bodyText, accentClass;
        if (decision.type === 'create_new') {
            icon = <Plus size={16} className="text-blue-600 flex-shrink-0" />;
            iconColor = 'text-blue-600';
            accentClass = 'border-blue-200 bg-blue-50';
            bodyText = <>Will be created as a new customer</>;
        } else if (decision.type === 'merge_with_suggestions') {
            // ROUND 7: decision can carry candidate-side targets, an existing-DB-
            // customer target, or both.
            const candidateNames = (decision.candidateSuggestionIndices || [])
                .map(idx => {
                    const sug = (candidate.suggestions || []).find(s => s.kind === 'candidate' && s.candidateIndex === idx);
                    return sug ? sug.displayName : '(unknown)';
                })
                .filter(Boolean);
            const dbName = decision.existingCustomerName || null;
            icon = <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />;
            iconColor = 'text-emerald-600';
            accentClass = 'border-emerald-300 bg-emerald-50';
            if (dbName && candidateNames.length === 0) {
                bodyText = (
                    <>
                        Will be matched to existing customer:{' '}
                        <span className="font-semibold text-emerald-900">{dbName}</span>
                    </>
                );
            } else if (dbName && candidateNames.length > 0) {
                bodyText = (
                    <>
                        Will be matched to existing customer{' '}
                        <span className="font-semibold text-emerald-900">{dbName}</span>
                        {' '}and merged with{' '}
                        <span className="font-semibold text-emerald-900">{candidateNames.join(', ')}</span>
                    </>
                );
            } else {
                bodyText = (
                    <>
                        Will be merged with{' '}
                        {candidateNames.length === 1 ? '' : `${candidateNames.length} other rows: `}
                        <span className="font-semibold text-emerald-900">{candidateNames.join(', ')}</span>
                    </>
                );
            }
        } else if (decision.type === 'match_to_existing') {
            icon = <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />;
            iconColor = 'text-emerald-600';
            accentClass = 'border-emerald-300 bg-emerald-50';
            bodyText = (
                <>
                    Will be matched to existing customer:{' '}
                    <span className="font-semibold text-emerald-900">{decision.customerName || '(customer)'}</span>
                </>
            );
        } else if (decision.type === 'match_to_candidate') {
            icon = <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />;
            iconColor = 'text-emerald-600';
            accentClass = 'border-emerald-300 bg-emerald-50';
            bodyText = (
                <>
                    Will be merged with another row from this import:{' '}
                    <span className="font-semibold text-emerald-900">{decision.targetName || '(another row)'}</span>
                </>
            );
        }

        return (
            <div className={`rounded-lg border p-3 flex items-center justify-between gap-3 ${accentClass}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                    {icon}
                    <div className="text-sm min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{candidate.displayName}</div>
                        <div className="text-xs text-gray-700 mt-0.5">{bodyText}</div>
                    </div>
                </div>
                <button
                    onClick={() => onReset(candidate.candidateIndex)}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1 flex-shrink-0"
                >
                    <Edit3 size={12} /> Change
                </button>
            </div>
        );
    }

    // ---- Active (decision-pending) state ----
    const totalLoaded = (existingCustomers || []).length + otherCandidates.length;
    const hasAnyResults = filteredExisting.length > 0 || filteredOtherCandidates.length > 0;
    const showSearchEmpty = showSearchResults && searchQuery.trim() && !hasAnyResults;

    // ROUND 7: toggle helpers for mixed-type checkboxes.
    const toggleCandidateCheck = (cIdx) => {
        setCheckedSuggestions(prev => {
            const exists = prev.find(s => s.kind === 'candidate' && s.candidateIndex === cIdx);
            if (exists) return prev.filter(s => !(s.kind === 'candidate' && s.candidateIndex === cIdx));
            return [...prev, { kind: 'candidate', candidateIndex: cIdx }];
        });
    };
    const toggleExistingCustomerCheck = (existingCustomerId, displayName) => {
        setCheckedSuggestions(prev => {
            const exists = prev.find(s => s.kind === 'existing_customer' && s.existingCustomerId === existingCustomerId);
            if (exists) {
                return prev.filter(s => !(s.kind === 'existing_customer' && s.existingCustomerId === existingCustomerId));
            }
            // Per-design rule: at most one existing_customer can be checked.
            // If user checks a different DB customer, replace.
            const withoutOtherDB = prev.filter(s => s.kind !== 'existing_customer');
            return [...withoutOtherDB, { kind: 'existing_customer', existingCustomerId, displayName }];
        });
    };
    const checkedCandidate = (cIdx) =>
        !!checkedSuggestions.find(s => s.kind === 'candidate' && s.candidateIndex === cIdx);
    const checkedExistingCustomer = (id) =>
        !!checkedSuggestions.find(s => s.kind === 'existing_customer' && s.existingCustomerId === id);

    const confirmMergeChecked = () => {
        if (checkedSuggestions.length === 0) return;
        const candidateSuggestionIndices = checkedSuggestions
            .filter(s => s.kind === 'candidate')
            .map(s => s.candidateIndex);
        const dbPick = checkedSuggestions.find(s => s.kind === 'existing_customer');
        onDecision(candidate.candidateIndex, {
            type: 'merge_with_suggestions',
            candidateSuggestionIndices,
            existingCustomerId: dbPick ? dbPick.existingCustomerId : null,
            existingCustomerName: dbPick ? dbPick.displayName : null,
        });
    };
    const handleCreateNew = () => {
        onDecision(candidate.candidateIndex, { type: 'create_new' });
    };
    const handlePickExisting = (c) => {
        onDecision(candidate.candidateIndex, {
            type: 'match_to_existing',
            existingCustomerId: c.id,
            customerName: c.name,
        });
        setSearchOpen(false);
        setSearchQuery('');
        setShowSearchResults(false);
    };
    const handlePickOtherCandidate = (c) => {
        onDecision(candidate.candidateIndex, {
            type: 'match_to_candidate',
            targetCandidateIndex: c.candidateIndex,
            targetName: c.name,
        });
        setSearchOpen(false);
        setSearchQuery('');
        setShowSearchResults(false);
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
            {/* Header: this candidate */}
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base font-semibold text-gray-900">{candidate.displayName}</span>
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                            {sourceLabel(candidate.source)}
                        </span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                        {candidate.email && <div className="truncate">{candidate.email}</div>}
                        {candidate.phone && <div>{candidate.phone}</div>}
                        {candidate.city && <div>{candidate.city}</div>}
                    </div>
                </div>
            </div>

            {/* Suggestions — Round 7: split into existing-DB-customer suggestions
                (high-stakes) and other-row-from-this-import suggestions */}
            {candidate.suggestions && candidate.suggestions.length > 0 && (() => {
                const existingDbSugs = candidate.suggestions.filter(s => s.kind === 'existing_customer');
                const candidateSugs = candidate.suggestions.filter(s => s.kind === 'candidate' || !s.kind);
                return (
                    <div className="border-t border-gray-100 pt-3 mb-3">
                        <p className="text-xs font-semibold text-gray-700 mb-2">
                            Possible duplicates {candidate.suggestions.length > 1 ? '— check all that are the same business:' : ':'}
                        </p>

                        {/* Section: existing customers in your DB (rendered first — higher stakes) */}
                        {existingDbSugs.length > 0 && (
                            <div className="mb-3">
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 mb-1.5 flex items-center gap-1">
                                    <Users size={10} />
                                    From your existing customers
                                </div>
                                <div className="space-y-2">
                                    {existingDbSugs.map(sug => {
                                        const isChecked = checkedExistingCustomer(sug.existingCustomerId);
                                        return (
                                            <label
                                                key={`db-${sug.existingCustomerId}`}
                                                className={`flex items-start gap-2.5 p-2 rounded-md cursor-pointer transition-colors ${
                                                    isChecked ? 'bg-amber-50 border border-amber-300' : 'bg-amber-50/40 hover:bg-amber-50 border border-amber-100'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => toggleExistingCustomerCheck(sug.existingCustomerId, sug.displayName)}
                                                    className="mt-1 h-4 w-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="text-sm font-medium text-gray-900">{sug.displayName}</span>
                                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                                            Your customer
                                                        </span>
                                                        <span className="text-[10px] text-gray-500 font-mono">
                                                            {Math.round((sug.similarity || 0) * 100)}% match
                                                        </span>
                                                    </div>
                                                    <div className="text-[11px] text-amber-800 mt-0.5 italic">
                                                        Already in your customer database — merging will inherit this customer's history.
                                                    </div>
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Section: other rows in this import */}
                        {candidateSugs.length > 0 && (
                            <div>
                                {existingDbSugs.length > 0 && (
                                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                                        From this import
                                    </div>
                                )}
                                <div className="space-y-2">
                                    {candidateSugs.map(sug => {
                                        const isChecked = checkedCandidate(sug.candidateIndex);
                                        return (
                                            <label
                                                key={`cand-${sug.candidateIndex}`}
                                                className={`flex items-start gap-2.5 p-2 rounded-md cursor-pointer transition-colors ${
                                                    isChecked ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => toggleCandidateCheck(sug.candidateIndex)}
                                                    className="mt-1 h-4 w-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="text-sm font-medium text-gray-900">{sug.displayName}</span>
                                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-white px-1.5 py-0.5 rounded">
                                                            {sourceLabel(sug.source)}
                                                        </span>
                                                        <span className="text-[10px] text-gray-500 font-mono">
                                                            {Math.round((sug.similarity || 0) * 100)}% match
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-gray-600 mt-0.5 space-x-2">
                                                        {sug.email && <span>{sug.email}</span>}
                                                        {sug.phone && <span>{sug.phone}</span>}
                                                        {sug.city && <span>{sug.city}</span>}
                                                    </div>
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {checkedSuggestions.length > 0 && (
                            <div className="mt-2.5 flex items-center justify-between gap-2 px-1">
                                <span className="text-xs text-emerald-700">
                                    {checkedSuggestions.length} selected — will be merged with this customer
                                </span>
                                <button
                                    onClick={confirmMergeChecked}
                                    className="px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                                >
                                    Confirm merge
                                </button>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Action zone: create new + search override */}
            <div className="border-t border-gray-100 pt-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <button
                        onClick={handleCreateNew}
                        className="px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors flex items-center gap-1.5"
                    >
                        <Plus size={12} />
                        Not a duplicate — create as new
                    </button>
                    {!searchOpen && (
                        <button
                            onClick={() => setSearchOpen(true)}
                            className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                        >
                            Or match to a different customer instead…
                        </button>
                    )}
                </div>

                {/* Inline search override */}
                {searchOpen && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                            <p className="text-xs text-gray-600">
                                Search your existing customers or other rows in this import.
                            </p>
                            <button
                                onClick={() => { setSearchOpen(false); setSearchQuery(''); setShowSearchResults(false); }}
                                className="text-xs text-gray-400 hover:text-gray-600"
                            >
                                Cancel
                            </button>
                        </div>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={totalLoaded === 0 ? 'Loading customers…' : 'Type a customer name…'}
                                value={searchQuery}
                                onChange={(e) => { setSearchQuery(e.target.value); setShowSearchResults(true); }}
                                onFocus={() => setShowSearchResults(true)}
                                onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                autoFocus
                            />
                            {showSearchResults && hasAnyResults && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-72 overflow-y-auto">
                                    {filteredExisting.length > 0 && (
                                        <>
                                            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-100">
                                                Your customers
                                            </div>
                                            {filteredExisting.map(c => (
                                                <button
                                                    key={`ex-${c.id}`}
                                                    onMouseDown={(e) => { e.preventDefault(); handlePickExisting(c); }}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b border-gray-100 last:border-b-0"
                                                >
                                                    {c.name}
                                                </button>
                                            ))}
                                        </>
                                    )}
                                    {filteredOtherCandidates.length > 0 && (
                                        <>
                                            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-100">
                                                Other rows in this import
                                            </div>
                                            {filteredOtherCandidates.map(c => (
                                                <button
                                                    key={`cand-${c.candidateIndex}`}
                                                    onMouseDown={(e) => { e.preventDefault(); handlePickOtherCandidate(c); }}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b border-gray-100 last:border-b-0"
                                                >
                                                    {c.name}
                                                </button>
                                            ))}
                                        </>
                                    )}
                                </div>
                            )}
                            {showSearchEmpty && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 px-3 py-2 text-xs text-gray-500 italic">
                                    {totalLoaded === 0
                                        ? 'No customers loaded. Check the browser console for errors.'
                                        : `No match in ${(existingCustomers || []).length} customers + ${otherCandidates.length} other rows. Try a different search term.`}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const ExecutePhase = ({ progress, plan, startTime, onCancel, cancelInFlight }) => {
    const total = progress.totalChunks || (plan?.summary?.toCreate ? Math.ceil((plan.summary.toCreate + plan.summary.toUpdate) / 200) : 0);
    const done = progress.chunksCompleted || 0;
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
    const stats = progress.stats || {};

    // Estimated time remaining (simple linear projection)
    const expectedTotalCustomers = (plan?.summary?.toCreate || 0) + (plan?.summary?.toUpdate || 0);
    const customersDone = (stats.customersCreated || 0) + (stats.customersUpdated || 0);
    let etaText = '';
    if (startTime && customersDone > 0 && expectedTotalCustomers > customersDone) {
        const elapsed = (Date.now() - startTime) / 1000;
        const rate = customersDone / elapsed;
        const remaining = (expectedTotalCustomers - customersDone) / rate;
        if (remaining > 0 && isFinite(remaining)) {
            etaText = remaining < 60
                ? `~${Math.round(remaining)} second${Math.round(remaining) === 1 ? '' : 's'} remaining`
                : `~${Math.round(remaining / 60)} minute${Math.round(remaining / 60) === 1 ? '' : 's'} remaining`;
        }
    }

    return (
        <div className="py-8">
            <div className="text-center mb-6">
                <Loader2 size={28} className="mx-auto text-indigo-500 animate-spin mb-3" />
                <p className="text-base font-medium text-gray-800">Importing your data…</p>
                {etaText && <p className="text-xs text-gray-500 mt-1">{etaText}</p>}
            </div>

            <div className="max-w-md mx-auto">
                <div className="mb-2 flex items-center justify-between text-xs text-gray-600">
                    <span>Chunk {done} of {total || '?'}</span>
                    <span className="font-medium">{pct}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 transition-all duration-300" style={{ width: `${pct}%` }} />
                </div>

                <div className="mt-6 space-y-2 text-sm text-gray-700">
                    <ProgressLine
                        label="Customers imported"
                        done={(stats.customersCreated || 0) + (stats.customersUpdated || 0)}
                        total={expectedTotalCustomers}
                    />
                    <ProgressLine
                        label="Opening invoices created"
                        done={stats.invoicesCreated || 0}
                        total={plan?.summary?.openingInvoicesCount || 0}
                    />
                </div>

                <div className="mt-6 flex justify-center">
                    <button
                        onClick={onCancel}
                        disabled={cancelInFlight}
                        className={`text-sm font-medium ${
                            cancelInFlight ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:text-red-600'
                        }`}
                    >
                        {cancelInFlight ? 'Cancelling…' : 'Cancel import'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ProgressLine = ({ label, done, total }) => (
    <div className="flex items-center justify-between gap-3">
        <span className="text-gray-700">{label}</span>
        <span className="font-mono text-xs">
            <span className="font-semibold text-gray-800">{done}</span>
            <span className="text-gray-400"> / </span>
            <span className="text-gray-600">{total}</span>
        </span>
    </div>
);

const CompletedPhase = ({
    progress, detectionFiles, invPlanError, dealPlanError,
    onContinueToInvoices, onContinueToDeals,
    onGoToCustomers, onStartOver,
}) => {
    const stats = progress.stats || {};
    const customersCreated = stats.customersCreated || 0;
    const customersUpdated = stats.customersUpdated || 0;
    const totalCustomers = customersCreated + customersUpdated;
    // ROUND 3b VERIFIER: 1761744000-round3b-completedphase
    // Detect whether this import included qb_sales files. If so, offer the
    // Continue-to-invoices CTA as the primary action.
    const hasInvoiceFiles = (detectionFiles || []).some(f =>
        f.sourceId === 'qb_sales' && f.mappingMode !== 'failed'
    );
    // ROUND 4b VERIFIER: 1761900000-round4b-deals-wizard
    // Detect whether this import included pipedrive_deals files. CTA cascade
    // priority: invoices first (if present), then jobs & follow-ups. The
    // jobs & follow-ups CTA only appears here directly when there are NO
    // invoice files — otherwise it appears after the invoice flow ends
    // (InvCompletedPhase).
    const hasDealFiles = (detectionFiles || []).some(f =>
        f.sourceId === 'pipedrive_deals' && f.mappingMode !== 'failed'
    );
    const showInvoiceCTA = hasInvoiceFiles;
    const showDealCTA = !hasInvoiceFiles && hasDealFiles;
    const showAnyCTA = showInvoiceCTA || showDealCTA;

    // ROUND 5d-fix VERIFIER: 1762082500-round5d-summary-text-clarity
    // Old text said "476 customers added to your business" which conflated
    // creates with updates. On a re-import where most rows match existing
    // customers, this implied false duplication. Now we surface the two
    // counts distinctly.
    const summaryText = (() => {
        if (customersCreated > 0 && customersUpdated > 0) {
            return `${customersCreated} ${customersCreated === 1 ? 'customer' : 'customers'} added, ${customersUpdated} matched and updated.`;
        }
        if (customersCreated > 0) {
            return `${customersCreated} ${customersCreated === 1 ? 'customer' : 'customers'} added to your business.`;
        }
        if (customersUpdated > 0) {
            return `${customersUpdated} ${customersUpdated === 1 ? 'customer' : 'customers'} matched and updated.`;
        }
        return 'No customer changes were applied.';
    })();

    return (
        <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 size={32} className="text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
                {showAnyCTA ? 'Customers imported!' : 'Import complete!'}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
                {summaryText}
                {(stats.invoicesCreated || 0) > 0 && (
                    <> {stats.invoicesCreated} opening {stats.invoicesCreated === 1 ? 'invoice' : 'invoices'} created.</>
                )}
            </p>

            {showInvoiceCTA && (
                <>
                    <div className="max-w-md mx-auto mb-5 p-4 bg-indigo-50 border border-indigo-200 rounded-xl text-left">
                        <div className="flex items-start gap-3">
                            <Receipt size={18} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-semibold text-indigo-900">Ready for invoice import</p>
                                <p className="text-xs text-indigo-700 mt-1">
                                    Your QuickBooks Sales data is uploaded. Continue to import invoices and payment history into your customer records.
                                </p>
                            </div>
                        </div>
                    </div>

                    {invPlanError && (
                        <div className="max-w-md mx-auto mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                            {invPlanError}
                        </div>
                    )}

                    <div className="flex items-center justify-center gap-3">
                        <button
                            onClick={onContinueToInvoices}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                        >
                            Continue to invoices
                            <ArrowRight size={16} />
                        </button>
                        <button
                            onClick={onGoToCustomers}
                            className="text-sm font-medium text-gray-600 hover:text-gray-800"
                        >
                            Skip for now
                        </button>
                    </div>
                </>
            )}

            {showDealCTA && (
                <>
                    <div className="max-w-md mx-auto mb-5 p-4 bg-indigo-50 border border-indigo-200 rounded-xl text-left">
                        <div className="flex items-start gap-3">
                            <Calendar size={18} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-semibold text-indigo-900">Ready to import jobs &amp; follow-ups</p>
                                <p className="text-xs text-indigo-700 mt-1">
                                    Your Pipedrive data is uploaded. Continue to import upcoming jobs, follow-up appointments with reminders, and historical activity into your customer records.
                                </p>
                            </div>
                        </div>
                    </div>

                    {dealPlanError && (
                        <div className="max-w-md mx-auto mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                            {dealPlanError}
                        </div>
                    )}

                    <div className="flex items-center justify-center gap-3">
                        <button
                            onClick={onContinueToDeals}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                        >
                            Continue to jobs &amp; follow-ups
                            <ArrowRight size={16} />
                        </button>
                        <button
                            onClick={onGoToCustomers}
                            className="text-sm font-medium text-gray-600 hover:text-gray-800"
                        >
                            Skip for now
                        </button>
                    </div>
                </>
            )}

            {!showAnyCTA && (
                <div className="flex items-center justify-center gap-3">
                    <button
                        onClick={onGoToCustomers}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                        Go to Customers
                        <ArrowRight size={16} />
                    </button>
                    <button
                        onClick={onStartOver}
                        className="text-sm font-medium text-gray-600 hover:text-gray-800"
                    >
                        Import more files
                    </button>
                </div>
            )}
        </div>
    );
};

const CancelledPhase = ({ progress, onGoToCustomers, onStartOver }) => {
    const stats = progress.stats || {};
    const totalCustomers = (stats.customersCreated || 0) + (stats.customersUpdated || 0);
    return (
        <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle size={32} className="text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Import cancelled</h3>
            <p className="text-sm text-gray-600 mb-6">
                {totalCustomers > 0 ? (
                    <>{totalCustomers} {totalCustomers === 1 ? 'customer was' : 'customers were'} imported before you cancelled. They remain in your business.</>
                ) : (
                    <>No customers were imported.</>
                )}
            </p>
            <div className="flex items-center justify-center gap-3">
                {totalCustomers > 0 && (
                    <button
                        onClick={onGoToCustomers}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                        Go to Customers
                        <ArrowRight size={16} />
                    </button>
                )}
                <button
                    onClick={onStartOver}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                    Start over
                </button>
            </div>
        </div>
    );
};

const FailedPhase = ({ error, progress, onGoToCustomers, onStartOver }) => {
    const stats = progress.stats || {};
    const totalCustomers = (stats.customersCreated || 0) + (stats.customersUpdated || 0);
    return (
        <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertOctagon size={32} className="text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Import failed</h3>
            {error && (
                <p className="text-sm text-red-700 mb-2 max-w-md mx-auto">{error}</p>
            )}
            {totalCustomers > 0 && (
                <p className="text-sm text-gray-600 mb-6">
                    {totalCustomers} {totalCustomers === 1 ? 'customer was' : 'customers were'} imported before the failure. They remain in your business.
                </p>
            )}
            <div className="flex items-center justify-center gap-3">
                {totalCustomers > 0 && (
                    <button
                        onClick={onGoToCustomers}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                        View imported customers
                        <ArrowRight size={16} />
                    </button>
                )}
                <button
                    onClick={onStartOver}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                    Start over
                </button>
            </div>
        </div>
    );
};

// ============================================================================
// Line Items (Products & Services) flow sub-components [seam c3a8e5f1]
// ============================================================================

const LineItemReviewPhase = ({ plan, imports, onToggle, onCancel, onStartImport, executeError }) => {
    const summary = plan?.summary || {};
    const reviewItems = plan?.reviewItems || [];
    const zeroPriceItems = reviewItems.filter(it => it.reason === 'no_price');
    const noNameItems = reviewItems.filter(it => it.reason === 'no_name');
    const deselectedCount = zeroPriceItems.filter(it => imports[it.key] === false).length;
    const importCount = Math.max(0, (summary.toCreate || 0) - deselectedCount);

    return (
        <div className="py-6">
            <div className="text-center mb-6">
                <div className="w-14 h-14 mx-auto bg-indigo-100 rounded-full flex items-center justify-center mb-3">
                    <FileSpreadsheet size={26} className="text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-1">Review your Products &amp; Services</h3>
                <p className="text-sm text-gray-600">
                    {summary.totalParsed || 0} items ready — {summary.products || 0} products, {summary.services || 0} services.
                    {(summary.toSkipExisting || 0) > 0 && (
                        <> {summary.toSkipExisting} already in your catalog will be skipped.</>
                    )}
                </p>
            </div>

            {zeroPriceItems.length > 0 && (
                <div className="max-w-2xl mx-auto mb-6">
                    <div className="flex items-start gap-2 mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-amber-800">
                            <span className="font-semibold">{zeroPriceItems.length} {zeroPriceItems.length === 1 ? 'item has' : 'items have'} no price set.</span>{' '}
                            Unchecked items won&apos;t be imported. We&apos;ve pre-unchecked ones that look like section headers rather than real items.
                        </p>
                    </div>
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-72 overflow-y-auto">
                        {zeroPriceItems.map((it) => (
                            <label key={it.key} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={imports[it.key] !== false}
                                    onChange={() => onToggle(it.key)}
                                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span className="flex-1 min-w-0">
                                    <span className="block text-sm font-medium text-gray-800 truncate">{it.name}</span>
                                    <span className="block text-xs text-gray-500">
                                        {it.type === 'Material' ? 'Product' : 'Service'}
                                        {it.cost != null && <> · cost ${Number(it.cost).toFixed(2)}</>}
                                    </span>
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {noNameItems.length > 0 && (
                <div className="max-w-2xl mx-auto mb-6 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                    {noNameItems.length} {noNameItems.length === 1 ? 'row has' : 'rows have'} no name and can&apos;t be imported — they&apos;ll be skipped.
                </div>
            )}

            {executeError && (
                <div className="max-w-2xl mx-auto mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {executeError}
                </div>
            )}

            <div className="flex items-center justify-center gap-3">
                <button
                    onClick={onCancel}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                    Cancel
                </button>
                <button
                    onClick={onStartImport}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                >
                    Import {importCount} {importCount === 1 ? 'item' : 'items'}
                    <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

const LineItemCompletedPhase = ({ progress, onStartOver }) => {
    const stats = progress.stats || {};
    const created = stats.created || 0;
    const skipped = stats.skippedExisting || 0;
    return (
        <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 size={32} className="text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Products &amp; Services imported!</h3>
            <p className="text-sm text-gray-600 mb-2">
                {created} {created === 1 ? 'item' : 'items'} added to your catalog.
                {skipped > 0 && <> {skipped} already existed and {skipped === 1 ? 'was' : 'were'} skipped.</>}
            </p>
            <p className="text-xs text-gray-500 mb-6">
                Find them under Settings → Products &amp; Services.
            </p>
            <div className="flex items-center justify-center gap-3">
                <button
                    onClick={onStartOver}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                >
                    Import more files
                    <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

// ============================================================================
// Line Items execute + cancelled sub-components [seam e7b1d4c9]
// ============================================================================
const LineItemExecutePhase = ({ progress, plan, startTime, onCancel, cancelInFlight }) => {
    const total = progress.totalChunks || 0;
    const done = progress.chunksCompleted || 0;
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
    const stats = progress.stats || {};
    const created = stats.created || 0;
    const skipped = stats.skippedExisting || 0;
    const expectedTotal = plan?.summary?.toCreate || 0;
    let etaText = '';
    if (startTime && created > 0 && expectedTotal > created) {
        const elapsed = (Date.now() - startTime) / 1000;
        const rate = created / elapsed;
        const remaining = (expectedTotal - created) / rate;
        if (remaining > 0 && isFinite(remaining)) {
            etaText = remaining < 60
                ? `~${Math.round(remaining)} second${Math.round(remaining) === 1 ? '' : 's'} remaining`
                : `~${Math.round(remaining / 60)} minute${Math.round(remaining / 60) === 1 ? '' : 's'} remaining`;
        }
    }
    return (
        <div className="py-8">
            <div className="text-center mb-6">
                <Loader2 size={28} className="mx-auto text-indigo-500 animate-spin mb-3" />
                <p className="text-base font-medium text-gray-800">Importing your Products &amp; Services…</p>
                {etaText && <p className="text-xs text-gray-500 mt-1">{etaText}</p>}
            </div>
            <div className="max-w-md mx-auto">
                <div className="mb-2 flex items-center justify-between text-xs text-gray-600">
                    <span>Batch {done} of {total || '?'}</span>
                    <span className="font-medium">{pct}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 transition-all duration-300" style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-6 space-y-2 text-sm text-gray-700">
                    <ProgressLine label="Items added" done={created} total={expectedTotal} />
                    {skipped > 0 && (
                        <ProgressLine label="Skipped (already existed)" done={skipped} total={skipped} />
                    )}
                </div>
                <div className="mt-6 flex justify-center">
                    <button
                        onClick={onCancel}
                        disabled={cancelInFlight}
                        className={`text-sm font-medium ${
                            cancelInFlight ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:text-red-600'
                        }`}
                    >
                        {cancelInFlight ? 'Cancelling…' : 'Cancel import'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const LineItemCancelledPhase = ({ progress, onStartOver }) => {
    const created = (progress.stats || {}).created || 0;
    return (
        <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle size={32} className="text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Import cancelled</h3>
            <p className="text-sm text-gray-600 mb-6">
                {created > 0
                    ? <>{created} {created === 1 ? 'item was' : 'items were'} added before you cancelled. They remain in your catalog.</>
                    : <>No items were imported.</>}
            </p>
            <div className="flex items-center justify-center gap-3">
                <button
                    onClick={onStartOver}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                    Start over
                </button>
            </div>
        </div>
    );
};

// ============================================================================
// ROUND 3b: Invoice flow sub-components
// ROUND 3b VERIFIER: 1761744000-round3b-subcomponents
// ============================================================================

const InvSummaryLine = ({ icon, label, value, valueClassName = '' }) => (
    <div className="flex items-start gap-2">
        <div className="mt-0.5">{icon}</div>
        <div className="flex-1">
            <span className="text-gray-600">{label}: </span>
            <span className={valueClassName}>{value}</span>
        </div>
    </div>
);

const InvPlanReviewPhase = ({ plan, onSkip, onContinue, invExecuteError }) => {
    const summary = plan.summary || {};
    const fileSummaries = plan.fileSummaries || [];
    const unresolvedGroups = plan.unresolvedGroups || [];
    const [filesExpanded, setFilesExpanded] = useState(false);
    const [errorsExpanded, setErrorsExpanded] = useState(false);

    // Has any anomaly worth surfacing?
    const hasOrphans = (summary.orphanPaymentCount || 0) > 0;
    const hasNegatives = (summary.negativeInvoiceCount || 0) > 0;
    const hasRefunds = (summary.refundCount || 0) > 0;
    const hasParseErrors = (summary.parseErrorCount || 0) > 0;
    const hasPartials = (summary.partialInvoiceCount || 0) > 0;

    const formatDateOnly = (iso) => {
        if (!iso) return '';
        const [y, m, d] = iso.split('-');
        const dt = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
        if (isNaN(dt.getTime())) return iso;
        return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <div>
            {/* Hero summary */}
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5 mb-5">
                <h3 className="text-base font-semibold text-indigo-900 flex items-center gap-2 mb-3">
                    <Receipt size={18} /> Invoice import plan
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
                    <div>
                        <div className="text-xs text-indigo-700 font-medium">Invoices</div>
                        <div className="text-2xl font-bold text-indigo-900">{summary.resolvedInvoiceCount || 0}</div>
                        <div className="text-xs text-gray-600">{formatCurrency(summary.resolvedInvoiceTotal || 0)}</div>
                    </div>
                    <div>
                        <div className="text-xs text-indigo-700 font-medium">Payments</div>
                        <div className="text-2xl font-bold text-indigo-900">{summary.resolvedPaymentCount || 0}</div>
                        <div className="text-xs text-gray-600">{formatCurrency(summary.resolvedPaymentTotal || 0)}</div>
                    </div>
                    <div>
                        <div className="text-xs text-indigo-700 font-medium">Date range</div>
                        <div className="text-sm font-semibold text-indigo-900 mt-1">
                            {summary.dateMin ? formatDateOnly(summary.dateMin) : '—'}
                        </div>
                        <div className="text-xs text-gray-600">to {summary.dateMax ? formatDateOnly(summary.dateMax) : '—'}</div>
                    </div>
                </div>

                <div className="text-xs text-indigo-700 mt-3 pt-3 border-t border-indigo-200">
                    <span className="font-semibold">{summary.statusBreakdown?.paid || 0}</span> paid
                    <span className="text-indigo-400 mx-2">·</span>
                    <span className="font-semibold">{summary.statusBreakdown?.unpaid || 0}</span> unpaid
                    {hasPartials && (
                        <>
                            <span className="text-indigo-400 mx-2">·</span>
                            <span className="font-semibold">{summary.statusBreakdown.partial}</span> partial
                        </>
                    )}
                </div>
            </div>

            {/* Unresolved groups callout (forward to next phase) */}
            {unresolvedGroups.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-5">
                    <div className="flex items-start gap-3">
                        <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <p className="text-sm font-semibold text-amber-900">
                                {summary.unresolvedInvoiceCount} {summary.unresolvedInvoiceCount === 1 ? 'invoice' : 'invoices'} need a customer assignment
                            </p>
                            <p className="text-xs text-amber-700 mt-1">
                                Grouped into {unresolvedGroups.length} {unresolvedGroups.length === 1 ? 'customer name' : 'customer names'} that didn't auto-match. You'll review these next.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Skipped / anomalies */}
            {(summary.voidedInvoiceCount > 0 || summary.voidedPaymentCount > 0
                || summary.zeroInvoiceCount > 0 || hasNegatives || hasOrphans) && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 mb-5">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <Info size={14} /> Skipped & anomalies
                    </h4>
                    <div className="space-y-1.5 text-xs">
                        {(summary.voidedInvoiceCount > 0) && (
                            <InvSummaryLine
                                icon={<X size={12} className="text-gray-400" />}
                                label="Voided invoices skipped"
                                value={summary.voidedInvoiceCount}
                                valueClassName="font-semibold text-gray-700"
                            />
                        )}
                        {(summary.voidedPaymentCount > 0) && (
                            <InvSummaryLine
                                icon={<X size={12} className="text-gray-400" />}
                                label="Voided payments skipped"
                                value={summary.voidedPaymentCount}
                                valueClassName="font-semibold text-gray-700"
                            />
                        )}
                        {(summary.zeroInvoiceCount > 0) && (
                            <InvSummaryLine
                                icon={<X size={12} className="text-gray-400" />}
                                label="Zero-amount invoices skipped"
                                value={summary.zeroInvoiceCount}
                                valueClassName="font-semibold text-gray-700"
                            />
                        )}
                        {hasNegatives && (
                            <InvSummaryLine
                                icon={<AlertTriangle size={12} className="text-amber-500" />}
                                label="Negative-amount invoices flagged"
                                value={summary.negativeInvoiceCount}
                                valueClassName="font-semibold text-amber-700"
                            />
                        )}
                        {hasOrphans && (
                            <InvSummaryLine
                                icon={<AlertTriangle size={12} className="text-amber-500" />}
                                label="Orphan payments skipped"
                                value={
                                    <span>
                                        {summary.orphanPaymentCount} ({formatCurrency(summary.orphanPaymentTotal || 0)})
                                        <span className="text-amber-600 ml-1">— customer name didn't match</span>
                                    </span>
                                }
                                valueClassName="font-semibold text-amber-700"
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Refunds (only if any) */}
            {hasRefunds && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 mb-5">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <RefreshCcw size={14} /> Refunds
                    </h4>
                    <p className="text-xs text-gray-600">
                        {summary.refundCount} {summary.refundCount === 1 ? 'refund' : 'refunds'} totaling{' '}
                        <span className="font-semibold">{formatCurrency(summary.refundTotal || 0)}</span>{' '}
                        will be imported as negative payment entries.
                    </p>
                </div>
            )}

            {/* Per-file breakdown (collapsed) */}
            <div className="rounded-xl border border-gray-200 bg-white mb-5">
                <button
                    onClick={() => setFilesExpanded(v => !v)}
                    className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
                >
                    <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <FileSpreadsheet size={14} /> Per-file breakdown ({fileSummaries.length} {fileSummaries.length === 1 ? 'file' : 'files'})
                    </span>
                    {filesExpanded ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronRight size={16} className="text-gray-500" />}
                </button>
                {filesExpanded && (
                    <div className="px-4 pb-4 pt-0 space-y-2 text-xs">
                        {fileSummaries.map((f, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-t border-gray-100">
                                <span className="text-gray-700 font-mono">{f.filename}</span>
                                <span className="text-gray-500">
                                    {f.error ? (
                                        <span className="text-red-600">{f.error}</span>
                                    ) : (
                                        <>{f.invoiceCount} invoices · {f.paymentCount} payments</>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Parse errors (collapsed, only if any) */}
            {hasParseErrors && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 mb-5">
                    <button
                        onClick={() => setErrorsExpanded(v => !v)}
                        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-amber-100"
                    >
                        <span className="text-sm font-medium text-amber-900 flex items-center gap-2">
                            <AlertTriangle size={14} /> {summary.parseErrorCount} {summary.parseErrorCount === 1 ? 'row' : 'rows'} skipped due to parse errors
                        </span>
                        {errorsExpanded ? <ChevronDown size={16} className="text-amber-700" /> : <ChevronRight size={16} className="text-amber-700" />}
                    </button>
                    {errorsExpanded && (plan.parseErrorsSample || []).length > 0 && (
                        <div className="px-4 pb-4 pt-0 space-y-1 text-xs">
                            {plan.parseErrorsSample.map((e, i) => (
                                <div key={i} className="py-1 border-t border-amber-200">
                                    <span className="font-mono text-amber-800">{e.filename || 'file'}, row {e.rowIndex}:</span>
                                    <span className="text-amber-700 ml-2">{e.reason}</span>
                                </div>
                            ))}
                            {summary.parseErrorCount > (plan.parseErrorsSample || []).length && (
                                <div className="pt-2 text-amber-600 italic">
                                    …and {summary.parseErrorCount - (plan.parseErrorsSample || []).length} more.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {invExecuteError && (
                <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {invExecuteError}
                </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between gap-4 pt-4 border-t border-gray-200">
                <button
                    onClick={onSkip}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                    Skip invoice import
                </button>
                <button
                    onClick={onContinue}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                >
                    {unresolvedGroups.length > 0 ? 'Continue to review' : 'Start invoice import'}
                    <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

// ----- InvUnresolvedGroupsPhase -----
// Grouped-by-name UI for invoices whose customer name didn't auto-match.
const InvUnresolvedGroupsPhase = ({
    plan, groupDecisions, invoiceOverrides, expandedGroups,
    existingCustomers, existingCustomersLoading,
    onGroupDecision, onGroupReset, onToggleExpanded, onInvoiceOverride,
    onBack, onStartImport, invExecuteError,
}) => {
    const groups = plan.unresolvedGroups || [];
    const decidedCount = groups.filter(g => groupDecisions[g.qbCustomerName]).length;
    const allDecided = decidedCount === groups.length;

    return (
        <div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-5">
                <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                        <h3 className="text-base font-semibold text-amber-900">
                            Resolve {groups.length} unmatched {groups.length === 1 ? 'customer' : 'customers'}
                        </h3>
                        <p className="text-xs text-amber-700 mt-1">
                            These QuickBooks customer names didn't match any of your existing customers. Assign each one to a customer, or skip the group entirely.
                        </p>
                    </div>
                    <div className="ml-auto text-xs font-medium text-amber-800 whitespace-nowrap">
                        {decidedCount} of {groups.length} decided
                    </div>
                </div>
            </div>

            {existingCustomersLoading && (
                <div className="text-sm text-gray-500 italic mb-3">Loading your customer list…</div>
            )}

            <div className="space-y-3 max-h-[700px] overflow-y-auto pr-1">
                {groups.map((group) => (
                    <UnresolvedGroupCard
                        key={group.qbCustomerName}
                        group={group}
                        decision={groupDecisions[group.qbCustomerName]}
                        invoiceOverrides={invoiceOverrides[group.qbCustomerName] || {}}
                        isExpanded={!!expandedGroups[group.qbCustomerName]}
                        existingCustomers={existingCustomers}
                        existingCustomersLoading={existingCustomersLoading}
                        onDecision={onGroupDecision}
                        onReset={onGroupReset}
                        onToggleExpanded={onToggleExpanded}
                        onInvoiceOverride={onInvoiceOverride}
                    />
                ))}
            </div>

            {invExecuteError && (
                <div className="mt-5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {invExecuteError}
                </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between gap-4 pt-4 mt-5 border-t border-gray-200">
                <button
                    onClick={onBack}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                    ← Back to plan summary
                </button>
                <button
                    onClick={onStartImport}
                    disabled={!allDecided}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                        allDecided
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                >
                    Start invoice import
                    <ArrowRight size={16} />
                </button>
            </div>

            {!allDecided && (
                <p className="text-xs text-gray-500 italic mt-2 text-right">
                    {groups.length - decidedCount} {groups.length - decidedCount === 1 ? 'group' : 'groups'} still need a decision before you can continue.
                </p>
            )}
        </div>
    );
};

const UnresolvedGroupCard = ({
    group, decision, invoiceOverrides, isExpanded, existingCustomers,
    existingCustomersLoading,
    onDecision, onReset, onToggleExpanded, onInvoiceOverride,
}) => {
    const isAssigned = decision?.action === 'assign';
    const isSkipped = decision?.action === 'skip';

    const formatDateRange = () => {
        if (!group.dateMin) return '';
        if (group.dateMin === group.dateMax) return formatDate(group.dateMin);
        const min = new Date(group.dateMin);
        const max = new Date(group.dateMax);
        return `${min.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })} – ${max.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`;
    };

    const assignedCustomer = isAssigned
        ? existingCustomers.find(c => c.id === decision.customerId)
        : null;

    return (
        <div className={`rounded-xl border p-4 ${
            isAssigned ? 'border-emerald-200 bg-emerald-50/50' :
            isSkipped ? 'border-gray-200 bg-gray-50' :
            'border-gray-200 bg-white'
        }`}>
            <div className="flex items-start gap-3 mb-2">
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">
                        "{group.qbCustomerName}"
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                        {group.invoiceCount} {group.invoiceCount === 1 ? 'invoice' : 'invoices'}
                        <span className="mx-1.5 text-gray-300">·</span>
                        {formatCurrency(group.totalAmount)}
                        <span className="mx-1.5 text-gray-300">·</span>
                        {formatDateRange()}
                    </div>
                </div>
                {!decision && (
                    <button
                        onClick={() => onDecision(group.qbCustomerName, { action: 'skip' })}
                        className="text-xs font-medium text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
                    >
                        Skip group
                    </button>
                )}
                {decision && (
                    <button
                        onClick={() => onReset(group.qbCustomerName)}
                        className="text-xs font-medium text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
                    >
                        Reset
                    </button>
                )}
            </div>

            {isAssigned && assignedCustomer && (
                <div className="flex items-center gap-2 text-sm text-emerald-700 mb-2 px-3 py-2 bg-emerald-100/60 rounded-lg">
                    <CheckCircle2 size={14} />
                    <span>Assigned to <span className="font-semibold">{assignedCustomer.name}</span></span>
                </div>
            )}

            {isSkipped && (
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2 px-3 py-2 bg-gray-100 rounded-lg">
                    <SkipForward size={14} />
                    <span>Skipping all invoices in this group</span>
                </div>
            )}

            {!decision && (
                <CustomerPicker
                    onSelect={(c) => onDecision(group.qbCustomerName, { action: 'assign', customerId: c.id })}
                    existingCustomers={existingCustomers}
                    isLoading={existingCustomersLoading}
                />
            )}

            {/* Per-invoice override expand */}
            {decision && group.invoices.length > 0 && (
                <button
                    onClick={() => onToggleExpanded(group.qbCustomerName)}
                    className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                >
                    {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    {isExpanded ? 'Hide' : 'Show'} {group.invoices.length} {group.invoices.length === 1 ? 'invoice' : 'invoices'}
                </button>
            )}

            {isExpanded && (
                <div className="mt-3 space-y-1 text-xs border-t border-gray-200 pt-3">
                    {group.invoices.map(inv => (
                        <InvoiceOverrideRow
                            key={inv.rowIndex}
                            invoice={inv}
                            override={invoiceOverrides[inv.rowIndex]}
                            groupDecision={decision}
                            existingCustomers={existingCustomers}
                            onOverride={(o) => onInvoiceOverride(group.qbCustomerName, inv.rowIndex, o)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// Note: the 'override' state flows in the wizard's invoiceOverrides ref
// for completeness, but for Round 3b we DON'T plumb per-invoice overrides
// to the backend — the manualAssignments contract is group-level only.
// Per-invoice override is reserved for a future round if/when it's needed
// in real customer migrations. The UI exposes the toggle so users can SEE
// individual invoices, but cannot reassign a single invoice within a group
// to a different customer without us shipping a Round 3c+ enhancement.
const InvoiceOverrideRow = ({ invoice, override, groupDecision, existingCustomers, onOverride }) => {
    const formatDateOnly = (iso) => {
        if (!iso) return '';
        const [y, m, d] = iso.split('-');
        const dt = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
        return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    };
    return (
        <div className="flex items-center gap-2 py-1">
            <span className="font-mono text-gray-500 w-16 truncate">
                #{invoice.legacyInvoiceNumber || '—'}
            </span>
            <span className="text-gray-600 w-24">{formatDateOnly(invoice.dateIso)}</span>
            <span className="text-gray-700 w-20 text-right tabular-nums">{formatCurrency(invoice.amount)}</span>
            <span className={`text-xs ml-2 px-1.5 py-0.5 rounded ${
                invoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                invoice.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-600'
            }`}>
                {invoice.status}
            </span>
        </div>
    );
};

const CustomerPicker = ({ onSelect, existingCustomers, isLoading }) => {
    const [query, setQuery] = useState('');
    const [showResults, setShowResults] = useState(false);

    const filtered = useMemo(() => {
        if (!query.trim()) return [];
        const q = query.toLowerCase();
        return existingCustomers
            .filter(c => c.name.toLowerCase().includes(q))
            .slice(0, 8);
    }, [query, existingCustomers]);

    // ROUND 3b VERIFIER: 1761744000-round3b-picker-emptystate-fix1
    // Diagnostic empty-state — distinguishes three failure modes:
    //   1. Still loading customer list
    //   2. Loaded zero customers (org is empty)
    //   3. Loaded customers, but none match the query
    let emptyMessage = null;
    if (showResults && query.trim() && filtered.length === 0) {
        if (isLoading) {
            emptyMessage = 'Loading your customers…';
        } else if (existingCustomers.length === 0) {
            emptyMessage = 'No customers loaded. Check the browser console for errors.';
        } else {
            emptyMessage = `No match in your ${existingCustomers.length} customers. Try a different search term.`;
        }
    }

    return (
        <div className="relative">
            <input
                type="text"
                placeholder={isLoading && existingCustomers.length === 0 ? 'Loading customers…' : 'Search your customers…'}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
                onFocus={() => setShowResults(true)}
                onBlur={() => setTimeout(() => setShowResults(false), 200)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            {showResults && filtered.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
                    {filtered.map(c => (
                        <button
                            key={c.id}
                            onMouseDown={(e) => { e.preventDefault(); onSelect(c); setQuery(''); setShowResults(false); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b border-gray-100 last:border-b-0"
                        >
                            {c.name}
                        </button>
                    ))}
                </div>
            )}
            {emptyMessage && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 px-3 py-2 text-xs text-gray-500 italic">
                    {emptyMessage}
                </div>
            )}
        </div>
    );
};

const InvExecutePhase = ({ progress, plan, startTime, onCancel, cancelInFlight }) => {
    const total = progress.totalChunks || 0;
    const done = progress.chunksCompleted || 0;
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
    const stats = progress.stats || {};
    const summary = plan?.summary || {};

    const expectedInvoices = summary.resolvedInvoiceCount || 0;
    const expectedPayments = summary.resolvedPaymentCount || 0;
    const totalRecordsExpected = expectedInvoices + expectedPayments;
    const recordsDone = (stats.invoicesCreated || 0) + (stats.paymentsCreated || 0);

    let etaText = '';
    if (startTime && recordsDone > 0 && totalRecordsExpected > recordsDone) {
        const elapsed = (Date.now() - startTime) / 1000;
        const rate = recordsDone / elapsed;
        const remaining = (totalRecordsExpected - recordsDone) / rate;
        if (remaining > 0 && isFinite(remaining)) {
            etaText = remaining < 60
                ? `~${Math.round(remaining)} second${Math.round(remaining) === 1 ? '' : 's'} remaining`
                : `~${Math.round(remaining / 60)} minute${Math.round(remaining / 60) === 1 ? '' : 's'} remaining`;
        }
    }

    const phaseLabel = progress.phase === 'A' ? 'Importing invoices…' : 'Importing payment history…';

    return (
        <div className="py-8">
            <div className="text-center mb-6">
                <Loader2 size={28} className="mx-auto text-indigo-500 animate-spin mb-3" />
                <p className="text-base font-medium text-gray-800">{phaseLabel}</p>
                {etaText && <p className="text-xs text-gray-500 mt-1">{etaText}</p>}
            </div>

            <div className="max-w-md mx-auto">
                <div className="mb-2 flex items-center justify-between text-xs text-gray-600">
                    <span>Chunk {done} of {total || '?'}</span>
                    <span className="font-medium">{pct}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 transition-all duration-300" style={{ width: `${pct}%` }} />
                </div>

                <div className="mt-6 space-y-2 text-sm text-gray-700">
                    <ProgressLine
                        label="Invoices imported"
                        done={stats.invoicesCreated || 0}
                        total={expectedInvoices}
                    />
                    <ProgressLine
                        label="Payments imported"
                        done={stats.paymentsCreated || 0}
                        total={expectedPayments}
                    />
                </div>

                <div className="mt-6 flex justify-center">
                    <button
                        onClick={onCancel}
                        disabled={cancelInFlight}
                        className={`text-sm font-medium ${
                            cancelInFlight ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:text-red-600'
                        }`}
                    >
                        {cancelInFlight ? 'Cancelling…' : 'Cancel import'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const InvCompletedPhase = ({
    progress, detectionFiles, dealPlanError,
    onContinueToDeals, onGoToCustomers, onStartOver,
}) => {
    const stats = progress.stats || {};
    // ROUND 4b VERIFIER: 1761900000-round4b-deals-wizard
    // After invoice flow ends, offer the deal CTA if pipedrive_deals files
    // are in this import session.
    const hasDealFiles = (detectionFiles || []).some(f =>
        f.sourceId === 'pipedrive_deals' && f.mappingMode !== 'failed'
    );

    return (
        <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 size={32} className="text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Invoice import complete!</h3>

            <div className="max-w-md mx-auto mb-5 grid grid-cols-2 gap-3 text-left">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <div className="text-2xl font-bold text-emerald-700">{stats.invoicesCreated || 0}</div>
                    <div className="text-xs text-emerald-600">invoices imported</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <div className="text-2xl font-bold text-emerald-700">{stats.paymentsCreated || 0}</div>
                    <div className="text-xs text-emerald-600">payments imported</div>
                </div>
            </div>

            <div className="max-w-md mx-auto mb-6 p-4 bg-gray-50 border border-gray-200 rounded-xl text-left text-xs text-gray-700 space-y-2">
                <div className="flex items-start gap-2">
                    <FileText size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />
                    <span>Imported invoices are in your Invoices list. Each shows both the original QuickBooks number and the new Spatchy number.</span>
                </div>
                <div className="flex items-start gap-2">
                    <Receipt size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />
                    <span>Each customer's payment history shows imported QuickBooks payments on their profile.</span>
                </div>
                <div className="flex items-start gap-2">
                    <Info size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Spatchy invoice numbers may take 1–2 minutes to fully assign. Refresh the Invoices list if you don't see numbers right away.</span>
                </div>
            </div>

            {hasDealFiles ? (
                <>
                    <div className="max-w-md mx-auto mb-5 p-4 bg-indigo-50 border border-indigo-200 rounded-xl text-left">
                        <div className="flex items-start gap-3">
                            <Calendar size={18} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-semibold text-indigo-900">Ready to import jobs &amp; follow-ups</p>
                                <p className="text-xs text-indigo-700 mt-1">
                                    Your Pipedrive data is uploaded. Continue to import upcoming jobs, follow-up appointments with reminders, and historical activity into your customer records.
                                </p>
                            </div>
                        </div>
                    </div>

                    {dealPlanError && (
                        <div className="max-w-md mx-auto mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                            {dealPlanError}
                        </div>
                    )}

                    <div className="flex items-center justify-center gap-3">
                        <button
                            onClick={onContinueToDeals}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                        >
                            Continue to jobs &amp; follow-ups
                            <ArrowRight size={16} />
                        </button>
                        <button
                            onClick={onGoToCustomers}
                            className="text-sm font-medium text-gray-600 hover:text-gray-800"
                        >
                            Skip for now
                        </button>
                    </div>
                </>
            ) : (
                <div className="flex items-center justify-center gap-3">
                    <button
                        onClick={onGoToCustomers}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                        Go to Customers
                        <ArrowRight size={16} />
                    </button>
                    <button
                        onClick={onStartOver}
                        className="text-sm font-medium text-gray-600 hover:text-gray-800"
                    >
                        Start over
                    </button>
                </div>
            )}
        </div>
    );
};

const InvCancelledPhase = ({ progress, onGoToCustomers, onStartOver }) => {
    const stats = progress.stats || {};
    const total = (stats.invoicesCreated || 0) + (stats.paymentsCreated || 0);
    return (
        <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle size={32} className="text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Invoice import cancelled</h3>
            <p className="text-sm text-gray-600 mb-6">
                {total > 0 ? (
                    <>
                        {stats.invoicesCreated || 0} {(stats.invoicesCreated || 0) === 1 ? 'invoice' : 'invoices'} and{' '}
                        {stats.paymentsCreated || 0} {(stats.paymentsCreated || 0) === 1 ? 'payment' : 'payments'} were imported before you cancelled.
                    </>
                ) : (
                    <>No invoices or payments were imported.</>
                )}
            </p>
            <div className="flex items-center justify-center gap-3">
                <button
                    onClick={onGoToCustomers}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                >
                    Go to Customers
                    <ArrowRight size={16} />
                </button>
                <button
                    onClick={onStartOver}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                    Start over
                </button>
            </div>
        </div>
    );
};

const InvFailedPhase = ({ error, progress, onGoToCustomers, onStartOver }) => {
    const stats = progress.stats || {};
    const total = (stats.invoicesCreated || 0) + (stats.paymentsCreated || 0);
    return (
        <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertOctagon size={32} className="text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Invoice import failed</h3>
            {error && (
                <p className="text-sm text-red-700 mb-2 max-w-md mx-auto">{error}</p>
            )}
            {total > 0 && (
                <p className="text-sm text-gray-600 mb-6">
                    {stats.invoicesCreated || 0} {(stats.invoicesCreated || 0) === 1 ? 'invoice' : 'invoices'} and{' '}
                    {stats.paymentsCreated || 0} {(stats.paymentsCreated || 0) === 1 ? 'payment' : 'payments'} were imported before the failure.
                </p>
            )}
            <div className="flex items-center justify-center gap-3">
                <button
                    onClick={onGoToCustomers}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                >
                    View imported customers
                    <ArrowRight size={16} />
                </button>
                <button
                    onClick={onStartOver}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                    Start over
                </button>
            </div>
        </div>
    );
};


// ============================================================================
// ROUND 4b VERIFIER: 1761900000-round4b-deals-wizard
// Round 4b: Jobs & follow-ups flow sub-components
// ============================================================================
//
// User-facing vocabulary in these components is intentionally Spatchy-native:
//   - "Scheduled jobs" (Closed Won, future-dated)
//   - "Pending jobs" (Meeting Agreed, future-dated)
//   - "Follow-up appointments" (snooze-date Follow-Ups)
//   - "Imported History records" (audit-only past-dated rows — surface in
//     customer Timeline tab via Round 4c)
// The word "deal" appears nowhere user-facing. Internal/code uses "deal"
// to match source-system terminology (Pipedrive Deals export).
// ============================================================================

const DealSummaryLine = ({ icon, label, value, valueClassName = '' }) => (
    <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 text-gray-600">
            {icon}
            <span>{label}</span>
        </div>
        <div className={valueClassName}>{value}</div>
    </div>
);

// ----- DealPlanReviewPhase -----
// Hero summary card showing what will happen during execute, with anomaly
// callout (skip toggle) and unresolved-orgs callout. Mirrors InvPlanReviewPhase
// design language but with Spatchy-native vocabulary throughout.
const DealPlanReviewPhase = ({
    plan, skipAnomalies, onToggleSkipAnomalies,
    onSkip, onContinue, dealExecuteError,
}) => {
    const summary = plan.summary || {};
    const fileSummaries = summary.fileSummaries || [];
    const unresolvedGroups = plan.unresolvedGroups || [];
    const promo = summary.promotionPreview || {};
    const [filesExpanded, setFilesExpanded] = useState(false);
    const [errorsExpanded, setErrorsExpanded] = useState(false);

    const hasParseErrors = (summary.parseErrorCount || 0) > 0;
    const totalPromotions = (promo.toClosed || 0) + (promo.toProposalSent || 0)
        + (promo.toContacted || 0) + (promo.toLost || 0);

    return (
        <div>
            {/* Hero summary */}
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5 mb-5">
                <h3 className="text-base font-semibold text-indigo-900 flex items-center gap-2 mb-3">
                    <Briefcase size={18} /> Jobs &amp; follow-ups to import from Pipedrive
                </h3>
                <p className="text-xs text-indigo-700 mb-4">
                    We found {summary.totalRowsParsed || 0} rows in your Pipedrive export. Here's what we'll create in Spatchy:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg border border-indigo-200 p-3">
                        <div className="flex items-center gap-1.5 text-xs text-indigo-700 font-medium mb-1">
                            <Calendar size={12} /> Scheduled
                        </div>
                        <div className="text-2xl font-bold text-indigo-900">{summary.toCreateScheduledJobs || 0}</div>
                        <div className="text-xs text-gray-600">jobs (won deals)</div>
                    </div>
                    <div className="bg-white rounded-lg border border-indigo-200 p-3">
                        <div className="flex items-center gap-1.5 text-xs text-indigo-700 font-medium mb-1">
                            <Clock size={12} /> Pending
                        </div>
                        <div className="text-2xl font-bold text-indigo-900">{summary.toCreatePendingJobs || 0}</div>
                        <div className="text-xs text-gray-600">jobs (meetings)</div>
                    </div>
                    <div className="bg-white rounded-lg border border-indigo-200 p-3">
                        <div className="flex items-center gap-1.5 text-xs text-indigo-700 font-medium mb-1">
                            <Bell size={12} /> Follow-ups
                        </div>
                        <div className="text-2xl font-bold text-indigo-900">{summary.toCreateFollowUps || 0}</div>
                        <div className="text-xs text-gray-600">with reminders</div>
                    </div>
                    <div className="bg-white rounded-lg border border-indigo-200 p-3">
                        <div className="flex items-center gap-1.5 text-xs text-indigo-700 font-medium mb-1">
                            <HistoryIcon size={12} /> History
                        </div>
                        <div className="text-2xl font-bold text-indigo-900">{summary.auditOnlyCount || 0}</div>
                        <div className="text-xs text-gray-600">imported records</div>
                    </div>
                </div>

                <div className="text-xs text-indigo-700 mt-4 pt-3 border-t border-indigo-200 space-y-1">
                    <div className="flex items-start gap-2">
                        <Calendar size={12} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span><span className="font-semibold">Scheduled jobs</span> appear in your Jobs list and on the calendar — confirmed work from your Pipedrive pipeline.</span>
                    </div>
                    <div className="flex items-start gap-2">
                        <Clock size={12} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span><span className="font-semibold">Pending jobs</span> are tentative — meetings agreed but not yet scheduled. Confirm and edit them as needed.</span>
                    </div>
                    <div className="flex items-start gap-2">
                        <Bell size={12} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span><span className="font-semibold">Follow-up appointments</span> with reminders fire a notification on the date you set in Pipedrive.</span>
                    </div>
                    <div className="flex items-start gap-2">
                        <HistoryIcon size={12} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span><span className="font-semibold">Imported History records</span> appear in each customer's Timeline tab — historical activity from before you used Spatchy.</span>
                    </div>
                </div>
            </div>

            {/* Customer status promotion preview */}
            {totalPromotions > 0 && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-5">
                    <div className="flex items-start gap-3">
                        <Users size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <p className="text-sm font-semibold text-emerald-900">
                                {totalPromotions} {totalPromotions === 1 ? 'customer status will update' : 'customer statuses will update'}
                            </p>
                            <p className="text-xs text-emerald-700 mt-1">
                                Based on your Pipedrive data, we'll move these customers to the right kanban column:
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                {(promo.toClosed || 0) > 0 && (
                                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-medium">
                                        {promo.toClosed} → Closed
                                    </span>
                                )}
                                {(promo.toProposalSent || 0) > 0 && (
                                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-medium">
                                        {promo.toProposalSent} → Proposal Sent
                                    </span>
                                )}
                                {(promo.toContacted || 0) > 0 && (
                                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-medium">
                                        {promo.toContacted} → Contacted
                                    </span>
                                )}
                                {(promo.toLost || 0) > 0 && (
                                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
                                        {promo.toLost} → Lost
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-emerald-600 mt-2 italic">
                                Existing statuses are only promoted, never demoted — manual changes you've made are preserved.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Anomaly callout with skip toggle */}
            {(summary.anomalyCount || 0) > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-5">
                    <div className="flex items-start gap-3">
                        <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <p className="text-sm font-semibold text-amber-900">
                                {summary.anomalyCount} unusual {summary.anomalyCount === 1 ? 'row' : 'rows'} found
                            </p>
                            <p className="text-xs text-amber-700 mt-1">
                                These deals are marked "Closed Lost" but have a future close date — semantically odd. They might be data errors in your Pipedrive export.
                            </p>
                            <label className="mt-3 flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={skipAnomalies}
                                    onChange={onToggleSkipAnomalies}
                                    className="w-4 h-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                                />
                                <span className="text-xs font-medium text-amber-900">
                                    Skip these — don't create jobs (recommended)
                                </span>
                            </label>
                            <p className="text-[11px] text-amber-700 mt-1.5 italic">
                                Either way, these {summary.anomalyCount} {summary.anomalyCount === 1 ? 'row' : 'rows'} appear as Imported History records on the customer Timeline.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Unresolved orgs callout (forward to next phase) */}
            {unresolvedGroups.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-5">
                    <div className="flex items-start gap-3">
                        <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <p className="text-sm font-semibold text-amber-900">
                                {summary.unresolvedCount} {summary.unresolvedCount === 1 ? 'row needs' : 'rows need'} a customer match
                            </p>
                            <p className="text-xs text-amber-700 mt-1">
                                Grouped into {unresolvedGroups.length} {unresolvedGroups.length === 1 ? 'organization name' : 'organization names'} that didn't auto-match your existing customers. You'll review these next.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Files breakdown */}
            <div className="rounded-xl border border-gray-200 bg-white mb-5">
                <button
                    onClick={() => setFilesExpanded(v => !v)}
                    className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
                >
                    <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <FileSpreadsheet size={14} /> {fileSummaries.length} {fileSummaries.length === 1 ? 'file' : 'files'} parsed
                    </span>
                    {filesExpanded ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronRight size={16} className="text-gray-500" />}
                </button>
                {filesExpanded && (
                    <div className="px-4 pb-4 pt-0 text-xs">
                        {fileSummaries.map((f, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-t border-gray-100">
                                <span className="text-gray-700 font-mono">{f.filename}</span>
                                <span className="text-gray-500">
                                    {f.error ? (
                                        <span className="text-red-600">{f.error}</span>
                                    ) : (
                                        <>{f.dealCount} rows{f.parseErrors > 0 ? ` · ${f.parseErrors} parse errors` : ''}</>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Parse errors (collapsed, only if any) */}
            {hasParseErrors && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 mb-5">
                    <button
                        onClick={() => setErrorsExpanded(v => !v)}
                        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-amber-100"
                    >
                        <span className="text-sm font-medium text-amber-900 flex items-center gap-2">
                            <AlertTriangle size={14} /> {summary.parseErrorCount} {summary.parseErrorCount === 1 ? 'row' : 'rows'} skipped due to parse errors
                        </span>
                        {errorsExpanded ? <ChevronDown size={16} className="text-amber-700" /> : <ChevronRight size={16} className="text-amber-700" />}
                    </button>
                    {errorsExpanded && (plan.parseErrors || []).length > 0 && (
                        <div className="px-4 pb-4 pt-0 space-y-1 text-xs">
                            {plan.parseErrors.slice(0, 20).map((e, i) => (
                                <div key={i} className="py-1 border-t border-amber-200">
                                    <span className="font-mono text-amber-800">{e.filename || 'file'}, row {e.rowIndex}:</span>
                                    <span className="text-amber-700 ml-2">{e.reason}</span>
                                </div>
                            ))}
                            {(plan.parseErrors || []).length > 20 && (
                                <div className="pt-2 text-amber-600 italic">
                                    …and {plan.parseErrors.length - 20} more.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {dealExecuteError && (
                <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {dealExecuteError}
                </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between gap-4 pt-4 border-t border-gray-200">
                <button
                    onClick={onSkip}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                    Skip jobs &amp; follow-ups import
                </button>
                <button
                    onClick={onContinue}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                >
                    {unresolvedGroups.length > 0 ? 'Continue to review' : 'Start import'}
                    <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

// ----- DealUnresolvedGroupsPhase -----
// Grouped-by-organization-name UI for deals whose org name didn't auto-match.
// Reuses the existing CustomerPicker subcomponent for the typeahead.
const DealUnresolvedGroupsPhase = ({
    plan, groupDecisions, expandedGroups,
    existingCustomers, existingCustomersLoading,
    onGroupDecision, onGroupReset, onToggleExpanded,
    onBack, onStartImport, dealExecuteError,
}) => {
    const groups = plan.unresolvedGroups || [];
    const decidedCount = groups.filter(g => groupDecisions[g.orgName]).length;
    const allDecided = decidedCount === groups.length;

    return (
        <div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-5">
                <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                        <h3 className="text-base font-semibold text-amber-900">
                            Resolve {groups.length} unmatched {groups.length === 1 ? 'organization' : 'organizations'}
                        </h3>
                        <p className="text-xs text-amber-700 mt-1">
                            These Pipedrive organization names didn't match any of your existing customers. Assign each one to a customer, or skip it. Skipped organizations will not have jobs, follow-ups, or history records created.
                        </p>
                    </div>
                    <div className="ml-auto text-xs font-medium text-amber-800 whitespace-nowrap">
                        {decidedCount} of {groups.length} decided
                    </div>
                </div>
            </div>

            {existingCustomersLoading && (
                <div className="text-sm text-gray-500 italic mb-3">Loading your customer list…</div>
            )}

            <div className="space-y-3 max-h-[700px] overflow-y-auto pr-1">
                {groups.map((group) => (
                    <DealUnresolvedGroupCard
                        key={group.orgName}
                        group={group}
                        decision={groupDecisions[group.orgName]}
                        isExpanded={!!expandedGroups[group.orgName]}
                        existingCustomers={existingCustomers}
                        existingCustomersLoading={existingCustomersLoading}
                        onDecision={onGroupDecision}
                        onReset={onGroupReset}
                        onToggleExpanded={onToggleExpanded}
                    />
                ))}
            </div>

            {dealExecuteError && (
                <div className="mt-5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {dealExecuteError}
                </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between gap-4 pt-4 mt-5 border-t border-gray-200">
                <button
                    onClick={onBack}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                    ← Back to plan summary
                </button>
                <button
                    onClick={onStartImport}
                    disabled={!allDecided}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                        allDecided
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                >
                    Start import
                    <ArrowRight size={16} />
                </button>
            </div>

            {!allDecided && (
                <p className="text-xs text-gray-500 italic mt-2 text-right">
                    {groups.length - decidedCount} {groups.length - decidedCount === 1 ? 'organization' : 'organizations'} still need a decision before you can continue.
                </p>
            )}
        </div>
    );
};

// ----- DealUnresolvedGroupCard -----
// Per-org card. Shows org name, sample rows (capped at 3 from backend),
// stage badges, and assign/skip controls.
const DealUnresolvedGroupCard = ({
    group, decision, isExpanded, existingCustomers, existingCustomersLoading,
    onDecision, onReset, onToggleExpanded,
}) => {
    const isAssigned = decision?.action === 'assign';
    const isSkipped = decision?.action === 'skip';
    const samples = group.sampleRows || [];

    // Stage badges aggregated across this group's sample rows
    const stages = Array.from(new Set(samples.map(s => s.stage).filter(Boolean)));
    const stageBadgeColor = (stage) => {
        if (stage === 'Closed Won') return 'bg-emerald-100 text-emerald-800 border-emerald-300';
        if (stage === 'Meeting Agreed') return 'bg-indigo-100 text-indigo-800 border-indigo-300';
        if (stage === 'Proposal / Quote sent') return 'bg-blue-100 text-blue-800 border-blue-300';
        if (stage === 'Contacted') return 'bg-purple-100 text-purple-800 border-purple-300';
        if (stage === 'Closed Lost') return 'bg-gray-100 text-gray-700 border-gray-300';
        return 'bg-gray-100 text-gray-700 border-gray-300';
    };

    return (
        <div className={`rounded-xl border p-4 transition-colors ${
            isAssigned ? 'border-emerald-200 bg-emerald-50' :
            isSkipped ? 'border-gray-200 bg-gray-50 opacity-70' :
            'border-gray-200 bg-white'
        }`}>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h4 className="text-sm font-semibold text-gray-900 truncate">{group.orgName || '(blank)'}</h4>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                            · {group.count} {group.count === 1 ? 'row' : 'rows'}
                        </span>
                    </div>
                    {group.contactName && (
                        <div className="text-xs text-gray-600 mb-1.5">Contact: {group.contactName}</div>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                        {stages.map(s => (
                            <span key={s} className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${stageBadgeColor(s)}`}>
                                {s}
                            </span>
                        ))}
                    </div>
                </div>
                {(isAssigned || isSkipped) && (
                    <button
                        onClick={() => onReset(group.orgName)}
                        className="text-xs text-gray-500 hover:text-gray-700 underline whitespace-nowrap"
                    >
                        Change
                    </button>
                )}
            </div>

            {/* Show capped-at-3 warning if backend says count > 3 */}
            {group.count > 3 && (
                <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-800">
                    <Info size={12} className="inline mr-1 -mt-0.5" />
                    This organization has {group.count} rows in your Pipedrive export, but only the first 3 will be imported when you assign a customer here. To import all {group.count}, the matching customer must already exist before you upload.
                </div>
            )}

            {samples.length > 0 && (
                <button
                    onClick={() => onToggleExpanded(group.orgName)}
                    className="text-xs text-gray-600 hover:text-gray-800 flex items-center gap-1 mb-3"
                >
                    {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    {isExpanded ? 'Hide' : 'Show'} sample rows ({samples.length})
                </button>
            )}

            {isExpanded && samples.length > 0 && (
                <div className="mb-3 space-y-1 text-xs bg-white rounded border border-gray-200 p-2">
                    {samples.map((s, i) => (
                        <div key={i} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-b-0">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${stageBadgeColor(s.stage)}`}>
                                {s.stage}
                            </span>
                            <span className="text-gray-600">
                                {s.closeDateIso}
                                {s.amount > 0 && <span className="ml-2 font-medium">{formatCurrency(s.amount)}</span>}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {!isAssigned && !isSkipped && (
                <div className="space-y-2">
                    <CustomerPicker
                        onSelect={(c) => onDecision(group.orgName, { action: 'assign', customerId: c.id, customerName: c.name })}
                        existingCustomers={existingCustomers}
                        isLoading={existingCustomersLoading}
                    />
                    <button
                        onClick={() => onDecision(group.orgName, { action: 'skip' })}
                        className="text-xs text-gray-600 hover:text-gray-800 flex items-center gap-1"
                    >
                        <SkipForward size={12} /> Skip this organization
                    </button>
                </div>
            )}

            {isAssigned && (
                <div className="text-xs text-emerald-700 bg-white border border-emerald-200 rounded px-3 py-2 flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-emerald-600" />
                    <span>Assigned to: <span className="font-semibold">{decision.customerName || '(customer)'}</span></span>
                </div>
            )}

            {isSkipped && (
                <div className="text-xs text-gray-500 italic">
                    Skipped — no jobs, follow-ups, or history records will be created for this organization.
                </div>
            )}
        </div>
    );
};

// ----- DealExecutePhase -----
// Three-phase progress display: Phase A (parent jobs), Phase B (follow-ups),
// Phase C (history records + customer status updates).
const DealExecutePhase = ({ progress, plan, startTime, onCancel, cancelInFlight }) => {
    const total = progress.totalChunks || 0;
    const done = progress.chunksCompleted || 0;
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
    const stats = progress.stats || {};
    const summary = plan?.summary || {};

    const expectedParents = summary.toCreateParentJobs || 0;
    const expectedFollowUps = summary.toCreateFollowUps || 0;
    const expectedHistory = (summary.totalRowsParsed || 0) - (summary.unresolvedCount || 0);
    const totalRecordsExpected = expectedParents + expectedFollowUps + expectedHistory;
    const recordsDone = (stats.parentJobsCreated || 0) + (stats.followUpJobsCreated || 0) + (stats.auditDocsCreated || 0);

    let etaText = '';
    if (startTime && recordsDone > 0 && totalRecordsExpected > recordsDone) {
        const elapsed = (Date.now() - startTime) / 1000;
        const rate = recordsDone / elapsed;
        const remaining = (totalRecordsExpected - recordsDone) / rate;
        if (remaining > 0 && isFinite(remaining)) {
            etaText = remaining < 60
                ? `~${Math.round(remaining)} second${Math.round(remaining) === 1 ? '' : 's'} remaining`
                : `~${Math.round(remaining / 60)} minute${Math.round(remaining / 60) === 1 ? '' : 's'} remaining`;
        }
    }

    let phaseLabel = 'Importing jobs…';
    if (progress.phase === 'B') phaseLabel = 'Importing follow-up appointments…';
    if (progress.phase === 'C') phaseLabel = 'Importing history & updating customer statuses…';

    return (
        <div className="py-8">
            <div className="text-center mb-6">
                <Loader2 size={28} className="mx-auto text-indigo-500 animate-spin mb-3" />
                <p className="text-base font-medium text-gray-800">{phaseLabel}</p>
                {etaText && <p className="text-xs text-gray-500 mt-1">{etaText}</p>}
            </div>

            <div className="max-w-md mx-auto">
                <div className="mb-2 flex items-center justify-between text-xs text-gray-600">
                    <span>Chunk {done} of {total || '?'}</span>
                    <span className="font-medium">{pct}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 transition-all duration-300" style={{ width: `${pct}%` }} />
                </div>

                <div className="mt-6 space-y-2 text-sm text-gray-700">
                    <ProgressLine
                        label="Jobs imported"
                        done={stats.parentJobsCreated || 0}
                        total={expectedParents}
                    />
                    <ProgressLine
                        label="Follow-up appointments"
                        done={stats.followUpJobsCreated || 0}
                        total={expectedFollowUps}
                    />
                    <ProgressLine
                        label="Imported History records"
                        done={stats.auditDocsCreated || 0}
                        total={expectedHistory}
                    />
                </div>

                <div className="mt-6 flex justify-center">
                    <button
                        onClick={onCancel}
                        disabled={cancelInFlight}
                        className={`text-sm font-medium ${
                            cancelInFlight ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:text-red-600'
                        }`}
                    >
                        {cancelInFlight ? 'Cancelling…' : 'Cancel import'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ----- DealCompletedPhase -----
// Success state with stat cards and post-import information.
const DealCompletedPhase = ({ progress, onGoToCustomers, onStartOver }) => {
    const stats = progress.stats || {};
    return (
        <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 size={32} className="text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Jobs &amp; follow-ups imported!</h3>

            <div className="max-w-lg mx-auto mb-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-left">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <div className="text-2xl font-bold text-emerald-700">{stats.parentJobsCreated || 0}</div>
                    <div className="text-xs text-emerald-600">jobs</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <div className="text-2xl font-bold text-emerald-700">{stats.followUpJobsCreated || 0}</div>
                    <div className="text-xs text-emerald-600">follow-ups</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <div className="text-2xl font-bold text-emerald-700">{stats.auditDocsCreated || 0}</div>
                    <div className="text-xs text-emerald-600">history records</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <div className="text-2xl font-bold text-emerald-700">{stats.statusPromotions || 0}</div>
                    <div className="text-xs text-emerald-600">status updates</div>
                </div>
            </div>

            <div className="max-w-md mx-auto mb-6 p-4 bg-gray-50 border border-gray-200 rounded-xl text-left text-xs text-gray-700 space-y-2">
                <div className="flex items-start gap-2">
                    <Calendar size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />
                    <span>Imported jobs are in your Jobs list and on the calendar. Pending jobs need confirmation; scheduled jobs are ready to go.</span>
                </div>
                <div className="flex items-start gap-2">
                    <Bell size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />
                    <span>Follow-up appointments will fire a bell notification on the date you set in Pipedrive.</span>
                </div>
                <div className="flex items-start gap-2">
                    <HistoryIcon size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />
                    <span>Imported History records appear on each customer's Timeline tab — historical activity from before Spatchy.</span>
                </div>
                <div className="flex items-start gap-2">
                    <Info size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Imported jobs have a placeholder line item. Open each one to confirm the service details, time, and assigned crew.</span>
                </div>
            </div>

            <div className="flex items-center justify-center gap-3">
                <button
                    onClick={onGoToCustomers}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                >
                    Go to Customers
                    <ArrowRight size={16} />
                </button>
                <button
                    onClick={onStartOver}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                    Start over
                </button>
            </div>
        </div>
    );
};

// ----- DealCancelledPhase -----
const DealCancelledPhase = ({ progress, onGoToCustomers, onStartOver }) => {
    const stats = progress.stats || {};
    const total = (stats.parentJobsCreated || 0) + (stats.followUpJobsCreated || 0) + (stats.auditDocsCreated || 0);
    return (
        <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle size={32} className="text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Import cancelled</h3>
            <p className="text-sm text-gray-600 mb-6">
                {total > 0 ? (
                    <>
                        {stats.parentJobsCreated || 0} {(stats.parentJobsCreated || 0) === 1 ? 'job' : 'jobs'},{' '}
                        {stats.followUpJobsCreated || 0} follow-{(stats.followUpJobsCreated || 0) === 1 ? 'up' : 'ups'}, and{' '}
                        {stats.auditDocsCreated || 0} history {(stats.auditDocsCreated || 0) === 1 ? 'record' : 'records'} were imported before you cancelled.
                    </>
                ) : (
                    <>No jobs, follow-ups, or history records were imported.</>
                )}
            </p>
            <div className="flex items-center justify-center gap-3">
                <button
                    onClick={onGoToCustomers}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                >
                    Go to Customers
                    <ArrowRight size={16} />
                </button>
                <button
                    onClick={onStartOver}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                    Start over
                </button>
            </div>
        </div>
    );
};

// ----- DealFailedPhase -----
const DealFailedPhase = ({ error, progress, onGoToCustomers, onStartOver }) => {
    const stats = progress.stats || {};
    const total = (stats.parentJobsCreated || 0) + (stats.followUpJobsCreated || 0) + (stats.auditDocsCreated || 0);
    return (
        <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertOctagon size={32} className="text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Import failed</h3>
            {error && (
                <p className="text-sm text-red-700 mb-2 max-w-md mx-auto">{error}</p>
            )}
            {total > 0 && (
                <p className="text-sm text-gray-600 mb-6">
                    {stats.parentJobsCreated || 0} {(stats.parentJobsCreated || 0) === 1 ? 'job' : 'jobs'},{' '}
                    {stats.followUpJobsCreated || 0} follow-{(stats.followUpJobsCreated || 0) === 1 ? 'up' : 'ups'}, and{' '}
                    {stats.auditDocsCreated || 0} history {(stats.auditDocsCreated || 0) === 1 ? 'record' : 'records'} were imported before the failure.
                </p>
            )}
            <div className="flex items-center justify-center gap-3">
                <button
                    onClick={onGoToCustomers}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                >
                    View imported customers
                    <ArrowRight size={16} />
                </button>
                <button
                    onClick={onStartOver}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                    Start over
                </button>
            </div>
        </div>
    );
};



export default ImportWizardV2;